import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calculator, Mail, ChevronRight, ChevronDown, ChevronUp, DollarSign, BarChart3, ArrowRight, Car, Wallet, AlertTriangle, Info, Scale, ChevronLeft, X, Grid, Target, MessageCircle, Lock, Percent, Clock, Plus, ArrowLeftRight, Lightbulb, BookOpen, Gauge, GitBranch, PiggyBank, MessageSquare, Copy, ArrowUpDown, CheckCircle2, Check, CheckCircle, AlertCircle, FileText, Share2, Wrench, List, PlusCircle, ClipboardList } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { quizQuestions } from '@/data/DecisionQuizData';
import TapToTypeValue from '@/components/TapToTypeValue';

// Design system constants - Premium FinTech aesthetic per spec
const CARD_STYLES = {
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  shadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  hoverTransform: 'translateY(-2px)',
  padding: '20px 24px',
  gap: '24px',
  maxWidth: '680px',
  transition: 'all 0.2s ease',
};

// Category-specific hover shadows (outer glow only, no background change)
const HOVER_SHADOWS = {
  path: '0 0 0 2px rgba(0, 31, 63, 0.15), 0 4px 20px rgba(0, 31, 63, 0.12)',
  budget: '0 0 0 2px rgba(0, 31, 63, 0.15), 0 4px 20px rgba(0, 31, 63, 0.12)',
  dealer: '0 0 0 2px rgba(255, 65, 54, 0.15), 0 4px 20px rgba(255, 65, 54, 0.12)',
};

// Hover background tints by category - REMOVED, keep white
const HOVER_TINTS = {
  path: '#FFFFFF',
  budget: '#FFFFFF',
  dealer: '#FFFFFF',
};

// Coming soon card styles (muted)
const COMING_SOON_STYLES = {
  opacity: 0.7,
  hoverShadow: '0px 3px 12px rgba(0, 0, 0, 0.10)',
  hoverTransform: 'translateY(-1px)',
};

// Updated category colors per new spec
const CATEGORY_COLORS = {
  path: '#001F3F',        // Navy - Choose a path
  budget: '#1F2937',      // Charcoal - Budget & costs
  dealer: '#FF4136',      // Red - Dealer Negotiation & Prep
};

const BRAND = {
  navy: '#001F3F',
  red: '#FF4136',
  green: '#10B981',
  purple: '#8E44AD',
};

// Icon Container - 40x40px rounded square with category gradient background
const ToolIcon = ({ icon: IconComponent, category }) => {
  // Gradients for each category - 135deg diagonal for depth
  const gradients = {
    path: 'linear-gradient(135deg, #001F3F, #2A52A0)',      // Navy gradient for Choose a Path (Start Here)
    budget: 'linear-gradient(135deg, #1F2937, #4B5563)',    // Charcoal gradient for Payment & Cost Calculators
    dealer: 'linear-gradient(180deg, #CC3333 0%, #FF4136 100%)',    // Red gradient for Dealer Negotiation & Prep
  };
  
  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: gradients[category] || 'linear-gradient(135deg, #1F2937, #4B5563)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IconComponent
        className="w-5 h-5"
        style={{ color: '#FFFFFF' }}
        strokeWidth={2}
      />
    </div>
  );
};

// Best Used Badge - pill style with category tint and bold label
const BestUsedBadge = ({ category, children }) => {
  const bgColors = {
    path: 'rgba(0, 31, 63, 0.08)',
    budget: 'rgba(31, 41, 55, 0.08)',
    dealer: 'rgba(255, 65, 54, 0.08)',
  };
  
  return (
    <div 
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '28px',
        padding: '0 12px',
        borderRadius: '14px',
        backgroundColor: bgColors[category] || '#F3F4F6',
        marginTop: '12px',
        fontSize: '13px',
        lineHeight: '1',
      }}
    >
      <span style={{ fontWeight: 700, color: '#374151', marginRight: '4px' }}>Best used:</span>
      <span style={{ color: '#6B7280' }}>{children}</span>
    </div>
  );
};

// Color Legend Component - Simple inline legend (no box)
const ColorLegend = () => (
  <div 
    style={{ 
      marginTop: '16px',
      display: 'flex', 
      flexWrap: 'wrap', 
      alignItems: 'center',
      gap: '16px',
      fontSize: '13px',
    }}
  >
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#001F3F' }} />
      <span style={{ color: '#6B7280' }}>Navy = Choose a path</span>
    </div>
    
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1F2937' }} />
      <span style={{ color: '#6B7280' }}>Charcoal = Budget & costs</span>
    </div>
    
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF4136' }} />
      <span style={{ color: '#6B7280' }}>Red = Dealer Negotiation & Prep</span>
    </div>
  </div>
);

// White text version for use in gradient banner
const ColorLegendWhite = () => (
  <div 
    style={{ 
      marginTop: '16px',
      display: 'flex', 
      flexWrap: 'wrap', 
      alignItems: 'center',
      gap: '16px',
      fontSize: '13px',
    }}
  >
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'white', border: '2px solid white' }} />
      <span style={{ color: 'white' }}>Navy = Choose a path</span>
    </div>
    
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#9CA3AF' }} />
      <span style={{ color: 'white' }}>Charcoal = Budget & costs</span>
    </div>
    
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF4136' }} />
      <span style={{ color: 'white' }}>Red = Dealer Negotiation & Prep</span>
    </div>
  </div>
);

// Charcoal text version for use in white card
const ColorLegendCharcoal = () => (
  <div 
    style={{ 
      marginTop: '16px',
      display: 'flex', 
      flexWrap: 'wrap', 
      alignItems: 'center',
      gap: '16px',
      fontSize: '13px',
    }}
  >
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#001F3F' }} />
      <span style={{ color: '#1F2937' }}>Navy = Choose a path</span>
    </div>
    
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1F2937' }} />
      <span style={{ color: '#1F2937' }}>Charcoal = Budget & costs</span>
    </div>
    
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF4136' }} />
      <span style={{ color: '#1F2937' }}>Red = Dealer Negotiation & Prep</span>
    </div>
  </div>
);

const PublicTools = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCalculator, setActiveCalculator] = useState(null);
  const [loanAmount, setLoanAmount] = useState('25000');
  const [apr1, setApr1] = useState('7');
  const [apr2, setApr2] = useState('5');
  const [term, setTerm] = useState('60');
  const [result, setResult] = useState(null);

  // Email generator state
  const [emailData, setEmailData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    vin: '',
    zip: '',
    hasPreApproval: true,
    purchaseTimeline: '',
    preferredContact: '',
    buyerName: '',
    buyerPhone: '',
    buyerEmail: '',
    dealerEmails: ''
  });
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [leadFormCopy, setLeadFormCopy] = useState('');

  // Auto Loan Calculator state
  const [autoLoanAmount, setAutoLoanAmount] = useState('25000');
  const [autoLoanTerm, setAutoLoanTerm] = useState('60');
  const [autoLoanApr, setAutoLoanApr] = useState('7');
  const [vehicleType, setVehicleType] = useState('new');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [extraPayment, setExtraPayment] = useState('0');
  const [autoLoanError, setAutoLoanError] = useState('');
  
  // Track applied "Ways to Improve" suggestions for cumulative stacking
  const [appliedSuggestions, setAppliedSuggestions] = useState({
    downPayment: false,
    apr: false,
    term: false,
  });
  const [originalLoanValues, setOriginalLoanValues] = useState({
    amount: '25000',
    apr: '7',
    term: '60',
  });
  const [totalDownPaymentApplied, setTotalDownPaymentApplied] = useState(0);

  // Car Budget Calculator state
  const [budgetData, setBudgetData] = useState({
    monthlyIncome: '6000',
    housing: '',
    groceries: '',
    utilities: '',
    otherEssentials: '',
    debtPayments: '',
    savingsGoal: '',
    discretionary: '',
    insurance: '',
    fuel: '',
    maintenance: '',
    riskTolerance: 50, // 0-100 slider
    loanTerm: '60',
    aprEstimate: '7'
  });
  const [budgetResult, setBudgetResult] = useState(null);
  const [budgetError, setBudgetError] = useState('');
  const [usedPlaceholders, setUsedPlaceholders] = useState([]);

  // Four Square Simulator state
  const [showFourSquare, setShowFourSquare] = useState(false);
  const [fourSquareIconSettled, setFourSquareIconSettled] = useState(false);
  const [fourSquareInputs, setFourSquareInputs] = useState({
    vehiclePrice: 35000,
    tradeIn: 5000,
    downPayment: 3000,
    apr: 7,
    termMonths: 60,
    addOns: 0,
  });
  const [fourSquareError, setFourSquareError] = useState('');
  const [fourSquareBeforeSnapshot, setFourSquareBeforeSnapshot] = useState(null);
  const [showFourSquareFormula, setShowFourSquareFormula] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);
  const [whatChanged, setWhatChanged] = useState(null);
  
  // Ref to store initial interest value for Four Square insight card
  const fourSquareInitialInterestRef = useRef(null);
  
  // Buy or Lease Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState('intro'); // 'intro', 'questions', 'results'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizScores, setQuizScores] = useState({ N: 0, U: 0, L: 0 });
  const [showFullPlan, setShowFullPlan] = useState(false);
  const [debateResponse, setDebateResponse] = useState(null);

  // Lease Calculator state
  const [showLeaseCalc, setShowLeaseCalc] = useState(false);
  const [showLeaseExplainer, setShowLeaseExplainer] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [leaseInputs, setLeaseInputs] = useState({
    msrp: 40000,
    sellingPrice: 38000,
    incentives: 0,
    cashDown: 2000,
    tradeEquity: 0,
    termMonths: 36,
    residualPercent: 55,
    moneyFactor: 0.00125,
    acquisitionFee: 695,
    docFee: 299,
    otherFees: 0,
    taxRate: 7,
    taxMode: 'monthly',
    annualMileage: 12000,
  });

  // Check Dealer Lease Quote state
  const [showLeaseChecker, setShowLeaseChecker] = useState(false);
  const [showLeaseCheckerExplainer, setShowLeaseCheckerExplainer] = useState(false);
  const [leaseCheckerTooltip, setLeaseCheckerTooltip] = useState(null);
  const [leaseCheckInputs, setLeaseCheckInputs] = useState({
    quotedPayment: 450,
    paymentIncludesTax: true,
    residualPercent: 55,
    residualDollar: 0,
    termMonths: 36,
    msrp: 40000,
    capCostOrSellingPrice: 38000, // Combined field for selling price or adjusted cap cost
    moneyFactor: 0.00125,
    taxRate: 7,
    taxMode: 'monthly',
  });
  const [leaseCheckResults, setLeaseCheckResults] = useState(null);
  const [leaseCheckerTooltipOpen, setLeaseCheckerTooltipOpen] = useState(null);

  // Auto-open modals from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openLeaseCalc') === 'true') {
      setShowLeaseCalc(true);
    }
    if (params.get('openLeaseChecker') === 'true') {
      setShowLeaseChecker(true);
    }
  }, [location.search]);
  
  // Four Square slider config
  const FOUR_SQUARE_SLIDER_CONFIG = {
    vehiclePrice: { min: 10000, max: 80000, step: 500 },
    tradeIn: { min: 0, max: 30000, step: 500 },
    downPayment: { min: 0, max: 20000, step: 500 },
    apr: { min: 0, max: 25, step: 0.5 },
    termMonths: { min: 12, max: 96, step: 6 },
    addOns: { min: 0, max: 10000, step: 250 },
  };
  
  const FOUR_SQUARE_LABELS = {
    vehiclePrice: 'Vehicle price',
    tradeIn: 'Trade-in value',
    downPayment: 'Down payment',
    apr: 'APR',
    termMonths: 'Term',
    addOns: 'Add-ons',
  };
  
  const FOUR_SQUARE_HELPERS = {
    vehiclePrice: 'Sticker or negotiated price',
    tradeIn: 'What dealer offers for your current car',
    downPayment: 'Cash you bring to the deal',
    apr: 'Interest rate as a percentage',
    termMonths: 'Loan length in months',
    addOns: 'Warranty, GAP, protection packages',
  };
  
  const FOUR_SQUARE_ICONS = {
    vehiclePrice: Car,
    tradeIn: ArrowLeftRight,
    downPayment: DollarSign,
    apr: Percent,
    termMonths: Clock,
    addOns: Plus,
  };

  // Placeholder defaults for budget calculator
  const BUDGET_PLACEHOLDERS = {
    housing: 2000,
    groceries: 600,
    utilities: 350,
    otherEssentials: 300,
    debtPayments: 300,
    savingsGoal: 500,
    discretionary: 400,
    insurance: 200,
    fuel: 200,
    maintenance: 100
  };

  const PLACEHOLDER_LABELS = {
    housing: 'Housing',
    groceries: 'Groceries',
    utilities: 'Utilities',
    otherEssentials: 'Other essentials',
    debtPayments: 'Debt payments',
    savingsGoal: 'Savings goal',
    discretionary: 'Discretionary',
    insurance: 'Insurance',
    fuel: 'Fuel',
    maintenance: 'Maintenance'
  };

  // Trigger Four Square icon animation when modal opens
  useEffect(() => {
    if (showFourSquare) {
      setFourSquareIconSettled(false);
      const timer = setTimeout(() => setFourSquareIconSettled(true), 50);
      return () => clearTimeout(timer);
    }
  }, [showFourSquare]);

  const calculateBudget = () => {
    setBudgetError('');
    setUsedPlaceholders([]);

    const income = parseFloat(budgetData.monthlyIncome);
    if (isNaN(income) || income <= 0) {
      setBudgetError('Please enter a valid monthly income greater than 0.');
      return;
    }

    const apr = parseFloat(budgetData.aprEstimate);
    if (isNaN(apr) || apr < 0) {
      setBudgetError('Please enter a valid APR (0 or greater).');
      return;
    }

    const termMonths = parseInt(budgetData.loanTerm);
    if (![36, 48, 60, 72].includes(termMonths)) {
      setBudgetError('Please select a valid loan term.');
      return;
    }

    // Helper to get value or placeholder
    const placeholdersUsed = [];
    const getValue = (field) => {
      const val = parseFloat(budgetData[field]);
      if (isNaN(val) || budgetData[field] === '') {
        placeholdersUsed.push(field);
        return BUDGET_PLACEHOLDERS[field];
      }
      return Math.max(0, val);
    };

    const housing = getValue('housing');
    const groceries = getValue('groceries');
    const utilities = getValue('utilities');
    const otherEssentials = getValue('otherEssentials');
    const debtPayments = getValue('debtPayments');
    const savingsGoal = getValue('savingsGoal');
    const discretionary = getValue('discretionary');
    const insurance = getValue('insurance');
    const fuel = getValue('fuel');
    const maintenance = getValue('maintenance');

    setUsedPlaceholders(placeholdersUsed);

    // Calculate committed spending
    const essentials = housing + groceries + utilities + otherEssentials + debtPayments;
    const wants = discretionary;
    const savings = savingsGoal;
    const committed = essentials + wants + savings;
    const committedPct = committed / income;

    // Running costs
    const running = insurance + fuel + maintenance;

    // Risk tolerance bands
    const risk = budgetData.riskTolerance;
    let bandMin, bandMax;
    if (risk <= 33) {
      bandMin = 0.08;
      bandMax = 0.12;
    } else if (risk <= 66) {
      bandMin = 0.10;
      bandMax = 0.14;
    } else {
      bandMin = 0.12;
      bandMax = 0.16;
    }

    // Warnings
    const warnings = [];
    if (committedPct >= 0.95) {
      warnings.push('Your budget leaves almost no buffer. Consider lowering expenses or choosing a very low-cost vehicle.');
      bandMin = Math.max(0.05, bandMin - 0.02);
      bandMax = Math.max(0.10, bandMax - 0.02);
    } else if (committedPct >= 0.85) {
      warnings.push('Your budget is already tight. Keep car costs conservative to protect your buffer.');
      bandMin = Math.max(0.05, bandMin - 0.02);
      bandMax = Math.max(0.10, bandMax - 0.02);
    }

    // Car total cost band
    const carTotalMin = income * bandMin;
    const carTotalMax = income * bandMax;

    // Payment band
    let paymentMin = Math.max(carTotalMin - running, 0);
    let paymentMax = Math.max(carTotalMax - running, 0);

    if (running > carTotalMax) {
      warnings.push('Your estimated insurance/fuel/maintenance already uses most of the safe car budget. Focus on lowering running costs or choosing a less expensive vehicle.');
    }

    // Invert payment to financed amount
    const r = apr / 100 / 12;
    const invertPayment = (payment) => {
      if (apr === 0) {
        return payment * termMonths;
      }
      return payment * (Math.pow(1 + r, termMonths) - 1) / (r * Math.pow(1 + r, termMonths));
    };

    const financedMin = invertPayment(paymentMin);
    const financedMax = invertPayment(paymentMax);

    // Buffer
    const bufferMin = income - committed - carTotalMax;
    const bufferMax = income - committed - carTotalMin;

    setBudgetResult({
      income,
      essentials,
      wants,
      savings,
      committed,
      committedPct,
      running,
      insurance,
      fuel,
      maintenance,
      carTotalMin,
      carTotalMax,
      carTotalMid: (carTotalMin + carTotalMax) / 2,
      paymentMin,
      paymentMax,
      financedMin,
      financedMax,
      bufferMin,
      bufferMax,
      warnings,
      termMonths,
      apr
    });
  };

  const tryLowerTerm = () => {
    setBudgetData(prev => ({ ...prev, loanTerm: '48' }));
    setTimeout(() => calculateBudget(), 100);
  };

  const tryLowerApr = () => {
    setBudgetData(prev => ({ ...prev, aprEstimate: '5' }));
    setTimeout(() => calculateBudget(), 100);
  };

  // Four Square Simulator calculations
  const calculateFourSquare = useCallback((inputs) => {
    const { vehiclePrice, tradeIn, downPayment, apr, termMonths, addOns } = inputs;
    
    // Validation
    if (vehiclePrice < 0 || tradeIn < 0 || downPayment < 0 || apr < 0 || addOns < 0) {
      return { error: 'Values cannot be negative.' };
    }
    if (termMonths < 12 || termMonths > 96) {
      return { error: 'Term must be between 12 and 96 months.' };
    }
    if (apr > 25) {
      return { error: 'APR cannot exceed 25%.' };
    }

    const amountFinanced = vehiclePrice + addOns - tradeIn - downPayment;
    
    if (amountFinanced <= 0) {
      return {
        amountFinanced: 0,
        monthlyPayment: 0,
        totalPaid: 0,
        totalInterest: 0,
        error: null,
      };
    }

    const r = apr / 100 / 12;
    let monthlyPayment;
    
    if (apr === 0) {
      monthlyPayment = amountFinanced / termMonths;
    } else {
      monthlyPayment = amountFinanced * (r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
    }

    const totalPaid = monthlyPayment * termMonths;
    const totalInterest = totalPaid - amountFinanced;

    return {
      amountFinanced,
      monthlyPayment,
      totalPaid,
      totalInterest,
      error: null,
    };
  }, []);

  const fourSquareResults = calculateFourSquare(fourSquareInputs);

  // Set initial interest reference once when component mounts (for insight card)
  useEffect(() => {
    if (fourSquareInitialInterestRef.current === null && fourSquareResults.totalInterest) {
      fourSquareInitialInterestRef.current = fourSquareResults.totalInterest;
    }
  }, []); // Empty dependency - only run once on mount

  const applyScenario = (scenarioType) => {
    // Snapshot current state as "before"
    const beforeResults = calculateFourSquare(fourSquareInputs);
    setFourSquareBeforeSnapshot({
      inputs: { ...fourSquareInputs },
      results: beforeResults,
    });

    setActiveScenario(scenarioType);

    if (scenarioType === 'lowerPayment') {
      // Lower payment trap: extend term by 12 months
      const newTerm = Math.min(84, fourSquareInputs.termMonths + 12);
      setFourSquareInputs(prev => ({
        ...prev,
        termMonths: newTerm,
      }));
      setWhatChanged({
        changes: [
          { label: 'Term', before: `${fourSquareInputs.termMonths} months`, after: `${newTerm} months` },
        ],
        scenarioName: 'Lower payment trap',
      });
    } else if (scenarioType === 'addOnBundle') {
      // Add on bundle: add $1500 in add-ons
      const newAddOns = fourSquareInputs.addOns + 1500;
      setFourSquareInputs(prev => ({
        ...prev,
        addOns: newAddOns,
      }));
      setWhatChanged({
        changes: [
          { label: 'Add-ons', before: `$${fourSquareInputs.addOns.toLocaleString()}`, after: `$${newAddOns.toLocaleString()}` },
        ],
        scenarioName: 'Add-on bundle',
      });
    } else if (scenarioType === 'tradeDownshift') {
      // Trade downshift: reduce trade value by $1000
      const newTradeIn = Math.max(0, fourSquareInputs.tradeIn - 1000);
      setFourSquareInputs(prev => ({
        ...prev,
        tradeIn: newTradeIn,
      }));
      setWhatChanged({
        changes: [
          { label: 'Trade-in', before: `$${fourSquareInputs.tradeIn.toLocaleString()}`, after: `$${newTradeIn.toLocaleString()}` },
        ],
        scenarioName: 'Trade downshift',
      });
    }
  };

  const resetFourSquare = () => {
    setFourSquareInputs({
      vehiclePrice: 35000,
      tradeIn: 5000,
      downPayment: 3000,
      apr: 7,
      termMonths: 60,
      addOns: 0,
    });
    setFourSquareBeforeSnapshot(null);
    setActiveScenario(null);
    setFourSquareError('');
    setWhatChanged(null);
  };

  // Buy or Lease Quiz functions
  const handleQuizAnswerSelect = (answer) => {
    const question = quizQuestions[currentQuestionIndex];
    setQuizAnswers(prev => ({
      ...prev,
      [question.id]: answer.label
    }));
    
    // Update scores
    setQuizScores(prev => ({
      N: prev.N + (answer.deltaN || 0),
      U: prev.U + (answer.deltaU || 0),
      L: prev.L + (answer.deltaL || 0),
    }));

    // Auto-advance after 300ms delay
    setTimeout(() => {
      if (currentQuestionIndex < quizQuestions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        setQuizStep('results');
      }
    }, 300);
  };

  const handleQuizNext = () => {
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setQuizStep('results');
    }
  };

  const handleQuizBack = () => {
    if (currentQuestionIndex > 0) {
      // Remove previous answer's scores
      const prevQuestion = quizQuestions[currentQuestionIndex - 1];
      const prevAnswerLabel = quizAnswers[prevQuestion.id];
      if (prevAnswerLabel) {
        const prevAnswer = prevQuestion.answers.find(a => a.label === prevAnswerLabel);
        if (prevAnswer) {
          setQuizScores(prev => ({
            N: prev.N - (prevAnswer.deltaN || 0),
            U: prev.U - (prevAnswer.deltaU || 0),
            L: prev.L - (prevAnswer.deltaL || 0),
          }));
        }
        setQuizAnswers(prev => {
          const newAnswers = { ...prev };
          delete newAnswers[prevQuestion.id];
          return newAnswers;
        });
      }
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const resetQuiz = () => {
    setQuizStep('intro');
    setCurrentQuestionIndex(0);
    setQuizAnswers({});
    setQuizScores({ N: 0, U: 0, L: 0 });
    setShowFullPlan(false);
  };

  const getQuizWinner = () => {
    const { N, U, L } = quizScores;
    // Tie-breaker: prefer Used, then New, then Lease
    if (U >= N && U >= L) return 'Used';
    if (N >= U && N >= L) return 'New';
    return 'Lease';
  };

  const getQuizExplanation = (winner) => {
    if (winner === 'New') {
      return "You value warranties, predictability, and minimal repair risk. You enjoy having the latest technology and customizing your vehicle.";
    } else if (winner === 'Used') {
      return "You see vehicles as long-term tools and prioritize total value. You prefer avoiding early depreciation and getting the best financial deal.";
    } else {
      return "You prefer upgrading vehicles regularly and maintaining predictable payments. You focus on monthly affordability and flexibility.";
    }
  };

  const getArchetype = (winner, scores) => {
    // Determine archetype based on winner and scoring patterns
    if (winner === 'Used') {
      // Check if they lean toward value or long-term ownership
      return scores.U > scores.N + 5 ? 'The Value Maximizer' : 'The Long-Term Owner';
    } else if (winner === 'Lease') {
      return scores.L > scores.N + 3 ? 'The Flexibility Driver' : 'The Payment Optimizer';
    } else {
      return scores.N > scores.U + 3 ? 'The Feature Enthusiast' : 'The Reliability Planner';
    }
  };

  // Lease Calculator functions
  const calculateLeasePayment = () => {
    const {
      msrp, sellingPrice, incentives, cashDown, tradeEquity,
      termMonths, residualPercent, moneyFactor, acquisitionFee,
      docFee, otherFees, taxRate, taxMode
    } = leaseInputs;

    // 1. Residual value = MSRP × residual_percent
    const residualValue = msrp * (residualPercent / 100);

    // 2. Gross cap cost = selling_price + capitalized_fees + addons_capitalized
    const capitalizedFees = acquisitionFee + docFee + otherFees;
    const grossCapCost = sellingPrice + capitalizedFees;

    // 3. Cap reduction = rebates + cash_down + positive_trade_equity
    const positiveTradeEquity = tradeEquity > 0 ? tradeEquity : 0;
    const capReduction = incentives + cashDown + positiveTradeEquity;

    // 4. Adjusted cap cost = gross_cap_cost - cap_reduction + negative_trade_equity
    const negativeTradeEquity = tradeEquity < 0 ? Math.abs(tradeEquity) : 0;
    const adjustedCapCost = grossCapCost - capReduction + negativeTradeEquity;

    // 5. Depreciation = (adjusted_cap_cost - residual_value) / term_months
    const depreciation = (adjustedCapCost - residualValue) / termMonths;

    // 6. Finance charge = (adjusted_cap_cost + residual_value) × money_factor
    const financeCharge = (adjustedCapCost + residualValue) * moneyFactor;

    // 7. Base payment = depreciation + finance_charge
    const basePayment = depreciation + financeCharge;

    // 8. Total monthly payment with tax
    const taxMultiplier = taxRate / 100;
    const totalMonthlyPayment = taxMode === 'monthly' 
      ? basePayment * (1 + taxMultiplier) 
      : basePayment;

    // Additional calculations
    const totalPaid = totalMonthlyPayment * termMonths + (taxMode === 'upfront' ? basePayment * termMonths * taxMultiplier : 0) + cashDown;
    const totalInterest = financeCharge * termMonths;

    // 10. MF to APR = money_factor × 2400
    const aprEquivalent = moneyFactor * 2400;

    return {
      residualValue,
      grossCapCost,
      capReduction,
      adjustedCapCost,
      depreciation,
      financeCharge,
      basePayment,
      totalMonthlyPayment,
      totalPaid,
      totalInterest,
      aprEquivalent,
    };
  };

  const leaseResults = calculateLeasePayment();

  // Quick Lease Deal Score helper - Soft slate blue background (#F0F4F8)
  const getLeaseScore = (monthlyPayment, msrp) => {
    if (!msrp || msrp === 0) return null;
    const ratio = (monthlyPayment / msrp) * 100;
    if (ratio <= 1.0) {
      return { label: 'Excellent', tier: 'Excellent', color: '#001F3F', bgColor: '#FFFFFF', borderColor: '#001F3F', ratio };
    } else if (ratio <= 1.25) {
      return { label: 'Good', tier: 'Good', color: '#001F3F', bgColor: '#FFFFFF', borderColor: '#001F3F', ratio };
    } else if (ratio <= 1.5) {
      return { label: 'Fair', tier: 'Fair', color: '#1F2937', bgColor: '#FFFFFF', borderColor: '#1F2937', ratio };
    } else {
      return { label: 'High', tier: 'High', color: '#FF4136', bgColor: '#FFFFFF', borderColor: '#FF4136', ratio };
    }
  };

  const leaseScore = getLeaseScore(leaseResults.totalMonthlyPayment, leaseInputs.msrp);

  // Tooltip definitions for lease calculator
  const leaseTooltips = {
    msrp: {
      title: 'MSRP (Sticker Price)',
      text: "The manufacturer's suggested retail price. The bank bases the residual on this number, not your negotiated price. That's why negotiating the selling price still saves you money every month."
    },
    sellingPrice: {
      title: 'Selling Price (Negotiated Price)',
      text: "This is the price you actually agree to pay the dealer — ideally well below MSRP. Every $1,000 lower here usually saves you about $30–$35 per month."
    },
    incentives: {
      title: 'Incentives / Rebates',
      text: "Discounts from the manufacturer or dealer. Check the manufacturer's official website first, then the dealer's site. Many can be stacked together to lower your payment."
    },
    cashDown: {
      title: 'Cash Down Payment (Cap Reduction)',
      text: "Money you pay upfront to lower your capitalized cost. This reduces the amount you finance, which lowers your monthly payment. However, unlike a car purchase, you're not building equity.\n\nStrong recommendation: avoid large cash down payments on a lease. Since you do not own the car, that money is at risk. If you crash it, total it, or end the lease early, you lose every dollar you put down. Almost all lease deals let you do a zero-dollar drive-off (roll everything into monthly payments instead). Putting money down does NOT build equity like it does when you buy."
    },
    tradeEquity: {
      title: 'Trade-In Equity',
      text: "Positive = the dealer owes you money (great — it lowers your payment). Negative = you owe more than the car is worth (this gets added to what you finance and increases your payment)."
    },
    leaseTerm: {
      title: 'Lease Term (Months)',
      text: "How long you keep the car. 36 months is most common for new cars. Typical options: 24, 36, 39, or 48 months. Longer terms usually lower the monthly payment but you pay more total over time."
    },
    residual: {
      title: 'Residual % of MSRP',
      text: "Bank residual (for comparison / simulation). The bank's estimate of what the car will be worth at lease end (as a % of the sticker price). Higher residual = lower monthly payment. Typical range for 36 months / 10-12k miles per year: 50–65% depending on the vehicle. The dealer must give you this exact % on the official lease worksheet."
    },
    moneyFactor: {
      title: 'Money Factor',
      text: "This is the interest rate written as a decimal. Multiply by 2400 to see the normal APR (example: 0.0025 × 2400 = 6%). Lower is better. Money factors vary by lender, model, credit tier, and current programs. Ask for the base buy-rate money factor in writing."
    },
    acquisitionFee: {
      title: 'Acquisition Fee',
      text: "The bank's one-time fee to set up the lease. Typical $595–$1,095. Usually rolled into payments and rarely negotiable."
    },
    docFee: {
      title: 'Doc Fee',
      text: "Dealer paperwork fee. Varies widely by dealer and state ($100–$1,000). Some states cap it — shop around for lower fees."
    },
    otherFees: {
      title: 'Other Capitalized Fees',
      text: "Add-ons you roll into the lease (warranty, protection packages, etc.). You pay interest on these, so they increase your monthly payment."
    },
    taxRate: {
      title: 'Tax Rate % and Tax Mode',
      text: "Most states tax the monthly payment. Some tax the full vehicle price upfront. Choose the method that matches your state or ask your dealer for the lease worksheet."
    },
    // Dealer question tooltips
    dealerResidual: {
      title: 'What is the exact residual %?',
      text: "This is the car's projected value at lease end. Higher residual = lower monthly payment."
    },
    dealerBuyRate: {
      title: "Is this the bank's buy-rate money factor or marked up?",
      text: "The buy-rate is the true bank rate. Anything higher is dealer profit."
    },
    dealerCapCost: {
      title: 'What is the adjusted cap cost?',
      text: "This is the negotiated price plus fees. Lower cap cost = lower payments."
    },
    dealerAddOns: {
      title: 'Are any add-ons rolled into the lease?',
      text: "Extended warranties or GAP insurance added here inflate your payment — negotiate them out."
    },
    dealerDisposition: {
      title: 'What is the disposition fee at the end?',
      text: "Usually $300–$500 fee when you return the car. Ask upfront so there are no surprises."
    }
  };

  // Info icon component for tooltips
  const InfoIcon = ({ tooltipKey }) => (
    <button
      onClick={() => setActiveTooltip(tooltipKey)}
      className="inline-flex items-center justify-center w-4 h-4 rounded-full ml-1.5 hover:opacity-70 transition-opacity"
      
    >
      <Info className="w-2.5 h-2.5" style={{ color: '#6B7280' }} strokeWidth={2.5} />
    </button>
  );

  // Check Dealer Lease Quote - Reverse Engineer
  const reverseEngineerLease = () => {
    const {
      quotedPayment, paymentIncludesTax, residualPercent,
      termMonths, msrp, moneyFactor, taxRate, capCostOrSellingPrice
    } = leaseCheckInputs;

    const results = { redFlags: [], impliedMF: null, impliedAPR: null, impliedCapCost: null };

    // Step 1: Strip tax from quoted payment if included
    const taxMultiplier = taxRate / 100;
    const basePayment = paymentIncludesTax
      ? quotedPayment / (1 + taxMultiplier)
      : quotedPayment;

    // Step 2: Calculate implied cap cost using the formula
    // Residual Dollar Amount = MSRP × (Residual % / 100)
    const residualDollarAmount = msrp * (residualPercent / 100);
    
    // Implied Cap Cost = (Base Payment - Residual Dollar Amount × (Money Factor - 1/Term)) / (1/Term + Money Factor)
    const termReciprocal = 1 / termMonths;
    const impliedCapCost = (basePayment - residualDollarAmount * (moneyFactor - termReciprocal)) / (termReciprocal + moneyFactor);
    
    // Round to nearest whole dollar
    results.impliedCapCost = Math.round(impliedCapCost);

    // Calculate implied money factor for verification (using standard lease formula)
    // Finance Charge = Base Payment - Depreciation
    // Depreciation = (Cap Cost - Residual) / Term
    const depreciation = (impliedCapCost - residualDollarAmount) / termMonths;
    const financeCharge = basePayment - depreciation;
    const impliedMF = financeCharge / (impliedCapCost + residualDollarAmount);
    
    results.impliedMF = impliedMF > 0 ? impliedMF : null;
    results.impliedAPR = impliedMF > 0 ? impliedMF * 2400 : null;

    // Store selling price for cap cost comparison
    results.sellingPriceInput = capCostOrSellingPrice;

    // Check for red flags
    // Money factor markup calculation
    // Extra Per Month = (Cap Cost Value + Residual Dollar Amount) × (User's Money Factor − 0.00125) × (1 + Tax Rate ÷ 100)
    const buyRateBaseline = 0.00125; // 3% APR baseline
    if (moneyFactor > buyRateBaseline) {
      const capCostValue = results.impliedCapCost; // Use implied cap cost since reverse engineer has been clicked
      const extraPerMonth = (capCostValue + residualDollarAmount) * (moneyFactor - buyRateBaseline) * (1 + taxMultiplier);
      results.redFlags.push(`Money factor looks marked up — could be $${Math.round(extraPerMonth)} extra per month`);
    }

    // Check if implied cap cost is more than $200 ABOVE the selling price entered
    // This indicates the dealer's numbers don't add up - the payment is too high for the stated price
    if (capCostOrSellingPrice > 0) {
      const capCostGap = impliedCapCost - capCostOrSellingPrice;
      if (capCostGap > 200) {
        results.redFlags.push(`Cap cost doesn't match — the implied price is $${results.impliedCapCost.toLocaleString()}, which is $${Math.round(capCostGap).toLocaleString()} more than the $${capCostOrSellingPrice.toLocaleString()} you entered. Ask the dealer to explain the difference.`);
      }
    }

    // Calculate Quick Lease Deal Score
    results.leaseScore = getLeaseScore(quotedPayment, msrp);

    setLeaseCheckResults(results);
  };

  // Format currency with commas and 2 decimals
  const formatCurrency = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Live auto loan calculation using useMemo
  const autoLoanResult = useMemo(() => {
    const P = parseFloat(autoLoanAmount);
    const n = parseInt(autoLoanTerm);
    const apr = parseFloat(autoLoanApr);
    const extra = parseFloat(extraPayment) || 0;

    // Validation - return null if inputs are invalid
    if (isNaN(P) || P <= 0) return null;
    if (isNaN(n) || n <= 0 || !Number.isInteger(n)) return null;
    if (isNaN(apr) || apr < 0) return null;
    if (extra < 0) return null;

    let monthlyPayment;
    const r = apr / 100 / 12;

    if (apr === 0) {
      monthlyPayment = P / n;
    } else {
      monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }

    const totalPaid = monthlyPayment * n;
    const totalInterest = totalPaid - P;

    let resultData = {
      monthlyPayment,
      totalPrincipal: P,
      totalInterest,
      totalPaid,
    };

    // Calculate with extra payment if > 0
    if (extra > 0 && apr > 0) {
      let balance = P;
      let months = 0;
      let totalInterestWithExtra = 0;
      const payment = monthlyPayment + extra;

      while (balance > 0 && months < 600) { // Cap at 600 months to prevent infinite loop
        const interestThisMonth = balance * r;
        const principalThisMonth = Math.min(payment - interestThisMonth, balance);
        
        if (principalThisMonth <= 0) {
          // Payment doesn't cover interest - break to prevent infinite loop
          break;
        }
        
        totalInterestWithExtra += interestThisMonth;
        balance -= principalThisMonth;
        months++;
      }

      const interestSaved = totalInterest - totalInterestWithExtra;
      const monthsSaved = n - months;
      
      resultData.newPayoffMonths = months;
      resultData.monthsSaved = monthsSaved;
      resultData.interestSaved = interestSaved;
      resultData.hasExtraPayment = true;
    } else if (extra > 0 && apr === 0) {
      // 0% APR with extra payments
      const newMonths = Math.ceil(P / (monthlyPayment + extra));
      const monthsSaved = n - newMonths;
      resultData.newPayoffMonths = newMonths;
      resultData.monthsSaved = monthsSaved;
      resultData.interestSaved = 0;
      resultData.hasExtraPayment = true;
    }

    return resultData;
  }, [autoLoanAmount, autoLoanTerm, autoLoanApr, extraPayment]);

  const calculateAPRSavings = () => {
    const principal = parseFloat(loanAmount);
    const months = parseInt(term);
    const rate1 = parseFloat(apr1) / 100 / 12;
    const rate2 = parseFloat(apr2) / 100 / 12;

    // Monthly payment formula: P * [r(1+r)^n] / [(1+r)^n - 1]
    // Guard for 0% APR: if rate is 0, payment = principal / months
    const payment1 = rate1 === 0 ? principal / months : (principal * rate1 * Math.pow(1 + rate1, months)) / (Math.pow(1 + rate1, months) - 1);
    const payment2 = rate2 === 0 ? principal / months : (principal * rate2 * Math.pow(1 + rate2, months)) / (Math.pow(1 + rate2, months) - 1);

    const totalCost1 = payment1 * months;
    const totalCost2 = payment2 * months;
    const interest1 = totalCost1 - principal;
    const interest2 = totalCost2 - principal;
    const savings = interest1 - interest2;
    const monthlySavings = payment1 - payment2;

    setResult({
      payment1: payment1.toFixed(2),
      payment2: payment2.toFixed(2),
      interest1: interest1.toFixed(2),
      interest2: interest2.toFixed(2),
      savings: savings.toFixed(2),
      monthlySavings: monthlySavings.toFixed(2)
    });
  };

  const generateOTDEmail = () => {
    const vehicleDesc = `${emailData.year} ${emailData.make} ${emailData.model}${emailData.trim ? ` ${emailData.trim}` : ''}`;
    const email = `Subject: Quote Request – ${vehicleDesc}

Hello,

I'm currently comparing offers on a ${vehicleDesc} and would appreciate receiving the full out-the-door price in writing, including:

• Vehicle selling price
• Dealer fees
• Taxes
• Registration/title
• Any required add-ons
• Total out-the-door price
${emailData.vin ? `\nVIN: ${emailData.vin}` : ''}

I am comparing offers from several dealerships and plan to move forward soon with the most competitive written quote.

I'm planning to purchase within the next few days if the numbers make sense.

Thank you for your time.

Best regards
${emailData.buyerName || '[Name]'}
ZIP Code: ${emailData.zip || '[ZIP]'}
${emailData.preferredContact ? `Preferred contact: ${emailData.preferredContact}` : ''}`;

    setGeneratedEmail(email);
    
    // Also generate the lead form copy
    const leadCopy = `Hello,

I'm shopping for a ${vehicleDesc} and comparing offers from several dealerships.

Could you please send me the full out-the-door price, including taxes, fees, and any required add-ons?

If the numbers make sense, I'm planning to move forward within the next few days.

Thank you.`;
    setLeadFormCopy(leadCopy);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  // Consistent input styling
  const inputClassName = "w-full px-3 py-2.5 rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-opacity-50";
  const inputStyle = {
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    color: '#374151',
  };

  // Set page title and hide Emergent badge on this page
  useEffect(() => {
    document.title = 'Tools – Free Car Buying Helpers';
    
    // Hide the Emergent badge on this standalone page
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    
    // Restore badge when leaving this page
    return () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.display = 'flex';
      }
    };
  }, []);

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#F8F9FB' }}>
      {/* Header */}
      <div 
        style={{ 
          background: 'linear-gradient(to bottom, #FFFFFF 0%, #FAFBFC 100%)',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <div style={{ maxWidth: CARD_STYLES.maxWidth, margin: '0 auto', padding: '16px 24px', minHeight: '110px' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3" style={{ minHeight: '100px' }}>
              <img 
                src="https://res.cloudinary.com/dmadm1tui/image/upload/v1774027766/VINdicated_Logo_NEW_-_No_name_pzbadg.png" 
                alt="VINdicated.ai" 
                style={{ 
                  height: '100px', 
                  width: 'auto', 
                  minHeight: '100px', 
                  maxHeight: '100px', 
                  display: 'block' 
                }}
                data-testid="vindicated-logo"
              />
            </div>
            <h1 
              className="text-2xl font-semibold" 
              style={{ color: BRAND.navy, letterSpacing: '-0.02em' }}
              data-testid="public-tools-title"
            >
              Tools
            </h1>
          </div>
        </div>
      </div>

      {/* Tools List */}
      <div style={{ maxWidth: CARD_STYLES.maxWidth, margin: '0 auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: CARD_STYLES.gap }}>
        
        {/* Intro Card - Clean white card */}
        <div 
          style={{ 
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(0,31,63,0.06)',
            marginBottom: '20px',
          }}
          data-testid="tools-intro-card"
        >
          <h2 
            style={{ 
              color: '#001F3F', 
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '8px',
              letterSpacing: '-0.01em',
            }}
          >
            Quick tools for smarter car buying
          </h2>
          <p 
            style={{ 
              color: '#1F2937', 
              fontSize: '14px',
              lineHeight: '1.6',
            }}
          >
            Use these anytime you want the numbers to make sense. Each tool explains the tradeoffs in plain English so you can decide with confidence.
          </p>
          <ColorLegendCharcoal />
        </div>

        {/* START HERE HERO SECTION */}
        <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h2 
            style={{ 
              color: BRAND.navy,
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '8px',
              letterSpacing: '-0.02em',
            }}
          >
            Start Here
          </h2>
          <p 
            style={{ 
              color: '#4B5563', 
              fontSize: '16px',
              lineHeight: '1.6',
            }}
          >
            Not sure where to begin? Start with your budget to get oriented fast.
          </p>
        </div>

        {/* 1. Can I afford this car? - Charcoal (Budget & costs) - PRIMARY ENTRY POINT */}
        <a
          href="/tools/budget"
          className="block group"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: '24px',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.budget;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
          data-testid="can-i-afford-link"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={PiggyBank} category="path" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: '17px', fontWeight: 700, color: BRAND.navy, marginBottom: '8px' }}>
                Can I afford this car?
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5', marginBottom: '12px' }}>
                Find the payment that will not wreck your month.
              </p>
              <p style={{ fontSize: '13px', color: '#1F2937', fontWeight: 500, fontStyle: 'italic' }}>
                Most people start here to find a safe monthly budget.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </a>

        {/* 2. Buy or Lease Quiz - Navy (Choose a path) */}
        <button
          onClick={() => setShowQuiz(true)}
          className="w-full overflow-hidden text-left group"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.path;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
          data-testid="buy-or-lease-quiz-btn"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={GitBranch} category="path" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                Buy or lease? Let&apos;s decide.
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                Answer a few quick questions about how you drive and what matters most to you. We&apos;ll show which choice (new, used, or lease) lines up best and why, so you pick the option that actually fits your life and avoids expensive buyer&apos;s remorse. This quiz takes less than 5 minutes.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </button>

        {/* SECTION 2: PAYMENT & COST CALCULATORS */}
        <div style={{ marginTop: '32px', marginBottom: '20px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h2 
            style={{ 
              color: BRAND.navy,
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '6px',
              letterSpacing: '-0.01em',
            }}
          >
            Payment & Cost Calculators
          </h2>
          <p 
            style={{ 
              color: '#6B7280', 
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            Crunch the real numbers before you commit.
          </p>
        </div>

        {/* 3. Loan Estimator - Charcoal (Budget & costs) - VINdicated Master Spec */}
        <div 
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.budget;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
        >
          <button
            onClick={() => setActiveCalculator(activeCalculator === 'autoloan' ? null : 'autoloan')}
            className="w-full text-left group"
            style={{ 
              padding: CARD_STYLES.padding,
              transition: CARD_STYLES.transition,
              backgroundColor: 'transparent',
            }}
            data-testid="loan-estimator-toggle"
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <ToolIcon icon={Calculator} category="budget" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                  What's My Real Monthly Payment?
                </h2>
                <div style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                  <p style={{ margin: 0 }}>See what the rate markup costs before it becomes your loan.</p>
                </div>
              </div>
              {activeCalculator === 'autoloan' ? (
                <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
              ) : (
                <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
              )}
            </div>
          </button>

          {/* VINdicated Loan Estimator - Master Spec Implementation - REDESIGNED */}
          {activeCalculator === 'autoloan' && (
            <div className="px-6 pb-6 pt-5 border-t" style={{ borderTopColor: '#E5E7EB', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Input Section - Budget Check Style */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Loan Amount Slider */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                      Loan Amount
                    </label>
                    <TapToTypeValue
                      value={Number(autoLoanAmount) || 25000}
                      onChange={(val) => setAutoLoanAmount(String(val))}
                      min={5000}
                      max={100000}
                      prefix="$"
                    />
                  </div>
                  <input
                    type="range"
                    min={5000}
                    max={100000}
                    step={1000}
                    value={autoLoanAmount || 25000}
                    onChange={(e) => setAutoLoanAmount(e.target.value)}
                    className="budget-slider"
                    style={{
                      width: '100%',
                      height: '6px',
                      cursor: 'pointer',
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(autoLoanAmount || 25000) - 5000) / 95000) * 100}%, #E5E7EB ${((Number(autoLoanAmount || 25000) - 5000) / 95000) * 100}%, #E5E7EB 100%)`,
                    }}
                    data-testid="loan-amount-input"
                  />
                  <p className="text-xs" style={{ color: '#9CA3AF', marginTop: '4px' }}>Vehicle price or amount financed</p>
                </div>

                {/* Term Slider */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                      Term (months)
                    </label>
                    <TapToTypeValue
                      value={Number(autoLoanTerm) || 60}
                      onChange={(val) => setAutoLoanTerm(String(val))}
                      min={12}
                      max={96}
                      suffix=" mo"
                    />
                  </div>
                  <input
                    type="range"
                    min={12}
                    max={96}
                    step={6}
                    value={autoLoanTerm || 60}
                    onChange={(e) => setAutoLoanTerm(e.target.value)}
                    className="budget-slider"
                    style={{
                      width: '100%',
                      height: '6px',
                      cursor: 'pointer',
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(autoLoanTerm || 60) - 12) / 84) * 100}%, #E5E7EB ${((Number(autoLoanTerm || 60) - 12) / 84) * 100}%, #E5E7EB 100%)`,
                    }}
                    data-testid="loan-term-input"
                  />
                  <p className="text-xs" style={{ color: '#9CA3AF', marginTop: '4px' }}>Length of your loan</p>
                </div>

                {/* APR Slider */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                        APR (%)
                      </label>
                      <span 
                        className="cursor-help" 
                        title="APR is the yearly interest rate on your loan."
                        style={{ color: '#9CA3AF' }}
                      >
                        <Info className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                    </div>
                    <TapToTypeValue
                      value={Number(autoLoanApr) || 7}
                      onChange={(val) => setAutoLoanApr(String(val))}
                      min={0}
                      max={25}
                      suffix="%"
                      decimals={1}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={25}
                    step={0.5}
                    value={autoLoanApr || 7}
                    onChange={(e) => setAutoLoanApr(e.target.value)}
                    className="budget-slider"
                    style={{
                      width: '100%',
                      height: '6px',
                      cursor: 'pointer',
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(autoLoanApr || 7)) / 25) * 100}%, #E5E7EB ${((Number(autoLoanApr || 7)) / 25) * 100}%, #E5E7EB 100%)`,
                    }}
                    data-testid="loan-apr-input"
                  />
                  <p className="text-xs" style={{ color: '#9CA3AF', marginTop: '4px' }}>Annual interest rate</p>
                </div>
              </div>

              {/* Extra Payments Section */}
              <div 
                style={{
                  backgroundColor: '#F8F9FA',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  padding: '20px',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
                  style={{ color: '#1F2937' }}
                  data-testid="extra-payments-toggle"
                >
                  {showAdvanced ? (
                    <ChevronUp className="w-4 h-4" style={{ color: '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4" style={{ color: '#6B7280' }} />
                  )}
                  <span>Make extra monthly payments</span>
                </button>
                
                {showAdvanced && (
                  <div className="mt-4">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                        Extra Payment
                      </label>
                      <TapToTypeValue
                        value={Number(extraPayment) || 0}
                        onChange={(val) => setExtraPayment(String(val))}
                        min={0}
                        max={2000}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={2000}
                      step={50}
                      value={extraPayment || 0}
                      onChange={(e) => setExtraPayment(e.target.value)}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(extraPayment || 0)) / 2000) * 100}%, #E5E7EB ${((Number(extraPayment || 0)) / 2000) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="extra-payment-input"
                    />
                    <p className="text-xs" style={{ color: '#9CA3AF', marginTop: '4px' }}>Additional amount paid each month</p>
                    {autoLoanResult && autoLoanResult.hasExtraPayment && (
                      <div className="mt-4 p-3" style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', border: '1px solid #10B981' }}>
                        <p className="text-sm" style={{ color: '#10B981' }}>
                          Loan paid off <strong>{autoLoanResult.monthsSaved} months earlier</strong>
                        </p>
                        <p className="text-sm" style={{ color: '#10B981' }}>
                          Interest saved <strong>${formatCurrency(autoLoanResult.interestSaved)}</strong>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Your Estimated Payment Summary - REDESIGNED Navy Gradient Background */}
              {autoLoanResult && (
                <div data-testid="estimated-payment-summary">
                  {/* Main Payment Card - Navy Gradient Background */}
                  <div 
                    style={{ 
                      background: 'linear-gradient(180deg, #001F3F 0%, #0A4A7A 100%)',
                      borderRadius: '16px',
                      padding: '24px',
                      marginBottom: '12px',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <DollarSign className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.7)' }} strokeWidth={2} />
                      <h3 className="font-semibold text-base" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        Your Estimated Payment
                      </h3>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div className="flex justify-between items-baseline">
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Monthly Payment:</span>
                        <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '32px' }}>${formatCurrency(autoLoanResult.monthlyPayment)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'rgba(255,255,255,0.7)' }}>Cost Per Day:</span>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>${(autoLoanResult.monthlyPayment / 30).toFixed(2)}</span>
                      </div>
                      
                      {/* White divider line */}
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: '12px', marginBottom: '0px' }}></div>
                      
                      {/* Interest Cost Banner - Red Gradient */}
                      <div 
                        style={{ 
                          background: 'linear-gradient(90deg, #FF4136 0%, #CC3333 100%)',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          marginTop: '12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500 }}>Interest Cost for This Loan:</span>
                        <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '18px' }}>${formatCurrency(autoLoanResult.totalInterest)}</span>
                      </div>
                    </div>
                  </div>
                  {/* Explanation below the card */}
                  <p className="text-sm" style={{ color: '#1F2937', paddingLeft: '4px' }}>
                    That means you will pay <strong>${formatCurrency(autoLoanResult.totalInterest)}</strong> in interest to borrow ${Number(autoLoanAmount).toLocaleString('en-US')}.
                  </p>
                </div>
              )}

              {/* Instant What-If Coaching Section - REDESIGNED with Cumulative Stacking */}
              {autoLoanResult && (
                <div 
                  style={{ 
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    padding: '24px',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                  }}
                  data-testid="what-if-coaching"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div 
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Wrench className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <h3 className="font-semibold text-base" style={{ color: '#1F2937' }}>
                        Ways to Improve This Loan
                      </h3>
                    </div>
                    {/* Reset all changes link */}
                    {(appliedSuggestions.downPayment || appliedSuggestions.apr || appliedSuggestions.term) && (
                      <button
                        onClick={() => {
                          setAutoLoanAmount(originalLoanValues.amount);
                          setAutoLoanApr(originalLoanValues.apr);
                          setAutoLoanTerm(originalLoanValues.term);
                          setAppliedSuggestions({ downPayment: false, apr: false, term: false });
                          setTotalDownPaymentApplied(0);
                        }}
                        className="text-xs font-medium transition-opacity hover:opacity-70"
                        style={{ color: '#6B7280', textDecoration: 'underline' }}
                        data-testid="reset-all-changes"
                      >
                        Reset all changes
                      </button>
                    )}
                  </div>
                  <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                    Small changes can make a big difference.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Suggestion 1: Add $2,000 to down payment - with cumulative stacking */}
                    {(() => {
                      const incrementAmount = 2000;
                      const newAmount = Math.max(0, Number(autoLoanAmount) - incrementAmount);
                      const r = (Number(autoLoanApr) / 100) / 12;
                      const n = Number(autoLoanTerm);
                      let newPayment = 0;
                      if (r > 0 && newAmount > 0 && n > 0) {
                        newPayment = newAmount * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
                      } else if (newAmount > 0 && n > 0) {
                        newPayment = newAmount / n;
                      }
                      
                      // Calculate cumulative label
                      const nextTotalDown = totalDownPaymentApplied + incrementAmount;
                      const labelText = totalDownPaymentApplied > 0
                        ? `Add another $${incrementAmount.toLocaleString()} to your down payment (total: $${nextTotalDown.toLocaleString()} down)`
                        : `Add $${incrementAmount.toLocaleString()} to your down payment`;
                      
                      return (
                        <div 
                          className="flex items-center justify-between p-4"
                          style={{ 
                            backgroundColor: '#FFFFFF', 
                            borderRadius: '12px', 
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                          }}
                        >
                          <div className="flex-1">
                            <p className="text-sm" style={{ color: '#374151' }}>
                              {labelText} → New payment: <strong>${newPayment.toFixed(0)}</strong>
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setAutoLoanAmount(String(newAmount));
                              setTotalDownPaymentApplied(prev => prev + incrementAmount);
                            }}
                            className="text-xs font-semibold px-4 py-2 rounded-full transition-all hover:opacity-90 active:scale-95 flex items-center gap-1"
                            style={{ color: '#FFFFFF', backgroundColor: '#001F3F' }}
                            data-testid="apply-down-payment"
                          >
                            <span>Apply this change</span>
                          </button>
                        </div>
                      );
                    })()}

                    {/* Suggestion 2: Improve APR to 6% - shows Applied state when done */}
                    {(() => {
                      const targetApr = 6;
                      const currentApr = Number(autoLoanApr);
                      
                      // If APR already at or below target, show Applied state
                      if (currentApr <= targetApr) {
                        if (!appliedSuggestions.apr) return null; // Never been applied, just hide
                        return (
                          <div 
                            className="flex items-center justify-between p-4"
                            style={{ 
                              backgroundColor: '#F9FAFB', 
                              borderRadius: '12px', 
                              border: '1px solid #E5E7EB',
                              boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                            }}
                          >
                            <div className="flex-1">
                              <p className="text-sm" style={{ color: '#6B7280' }}>
                                Improved APR to 6%
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#001F3F' }}>
                              <Check className="w-4 h-4" style={{ color: '#10B981' }} strokeWidth={2.5} />
                              <span>Applied</span>
                            </div>
                          </div>
                        );
                      }
                      
                      // Show active suggestion
                      const r = (targetApr / 100) / 12;
                      const amt = Number(autoLoanAmount);
                      const n = Number(autoLoanTerm);
                      let newPayment = 0;
                      if (r > 0 && amt > 0 && n > 0) {
                        newPayment = amt * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
                      } else if (amt > 0 && n > 0) {
                        newPayment = amt / n;
                      }
                      return (
                        <div 
                          className="flex items-center justify-between p-4"
                          style={{ 
                            backgroundColor: '#FFFFFF', 
                            borderRadius: '12px', 
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                          }}
                        >
                          <div className="flex-1">
                            <p className="text-sm" style={{ color: '#374151' }}>
                              Improve APR from {currentApr}% to 6% → New payment: <strong>${newPayment.toFixed(0)}</strong>
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setAutoLoanApr('6');
                              setAppliedSuggestions(prev => ({ ...prev, apr: true }));
                            }}
                            className="text-xs font-semibold px-4 py-2 rounded-full transition-all hover:opacity-90 active:scale-95 flex items-center gap-1"
                            style={{ color: '#FFFFFF', backgroundColor: '#001F3F' }}
                            data-testid="apply-apr-change"
                          >
                            <span>Apply this change</span>
                          </button>
                        </div>
                      );
                    })()}

                    {/* Suggestion 3: Extend term to 72 months - shows Applied state when done */}
                    {(() => {
                      const targetTerm = 72;
                      const currentTerm = Number(autoLoanTerm);
                      
                      // If term already at or above target, show Applied state
                      if (currentTerm >= targetTerm) {
                        if (!appliedSuggestions.term) return null; // Never been applied, just hide
                        return (
                          <div 
                            className="flex items-center justify-between p-4"
                            style={{ 
                              backgroundColor: '#F9FAFB', 
                              borderRadius: '12px', 
                              border: '1px solid #E5E7EB',
                              boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                            }}
                          >
                            <div className="flex-1">
                              <p className="text-sm" style={{ color: '#6B7280' }}>
                                Extended term to 72 months
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#001F3F' }}>
                              <Check className="w-4 h-4" style={{ color: '#10B981' }} strokeWidth={2.5} />
                              <span>Applied</span>
                            </div>
                          </div>
                        );
                      }
                      
                      // Show active suggestion
                      const r = (Number(autoLoanApr) / 100) / 12;
                      const amt = Number(autoLoanAmount);
                      let newPayment = 0;
                      if (r > 0 && amt > 0) {
                        newPayment = amt * (r * Math.pow(1 + r, targetTerm)) / (Math.pow(1 + r, targetTerm) - 1);
                      } else if (amt > 0) {
                        newPayment = amt / targetTerm;
                      }
                      return (
                        <div 
                          className="flex items-center justify-between p-4"
                          style={{ 
                            backgroundColor: '#FFFFFF', 
                            borderRadius: '12px', 
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                          }}
                        >
                          <div className="flex-1">
                            <p className="text-sm" style={{ color: '#374151' }}>
                              Extend term from {currentTerm} to 72 months → New payment: <strong>${newPayment.toFixed(0)}</strong>
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setAutoLoanTerm('72');
                              setAppliedSuggestions(prev => ({ ...prev, term: true }));
                            }}
                            className="text-xs font-semibold px-4 py-2 rounded-full transition-all hover:opacity-90 active:scale-95 flex items-center gap-1"
                            style={{ color: '#FFFFFF', backgroundColor: '#001F3F' }}
                            data-testid="apply-term-change"
                          >
                            <span>Apply this change</span>
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Loan Term Comparison Table - REDESIGNED */}
              {autoLoanResult && (
                <div 
                  style={{ 
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    padding: '24px',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                  }}
                  data-testid="loan-term-comparison"
                >
                  <h3 className="font-semibold text-sm mb-4" style={{ color: '#1F2937' }}>
                    How loan terms change the total cost
                  </h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'separate', borderSpacing: 0 }}>
                      <thead>
                        <tr style={{ backgroundColor: '#001F3F' }}>
                          <th style={{ textAlign: 'left', padding: '12px 12px', color: '#FFFFFF', fontWeight: 600, borderTopLeftRadius: '8px' }}>Term</th>
                          <th style={{ textAlign: 'right', padding: '12px 12px', color: '#FFFFFF', fontWeight: 600 }}>Monthly Payment</th>
                          <th style={{ textAlign: 'right', padding: '12px 12px', color: '#FFFFFF', fontWeight: 600, borderTopRightRadius: '8px' }}>Total Interest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[60, 72, 84].map((termVal, idx) => {
                          const r = (Number(autoLoanApr) / 100) / 12;
                          const amt = Number(autoLoanAmount);
                          let payment = 0;
                          let totalInterest = 0;
                          if (r > 0 && amt > 0) {
                            payment = amt * (r * Math.pow(1 + r, termVal)) / (Math.pow(1 + r, termVal) - 1);
                            totalInterest = (payment * termVal) - amt;
                          } else if (amt > 0) {
                            payment = amt / termVal;
                          }
                          const isCurrentTerm = Number(autoLoanTerm) === termVal;
                          const rowBg = isCurrentTerm ? 'rgba(0, 31, 63, 0.06)' : (idx % 2 === 1 ? '#F8F9FA' : '#FFFFFF');
                          return (
                            <tr 
                              key={termVal} 
                              style={{ 
                                backgroundColor: rowBg,
                              }}
                            >
                              <td style={{ padding: '14px 12px', color: '#1F2937', fontWeight: isCurrentTerm ? 700 : 400, borderBottom: '1px solid #E5E7EB' }}>{termVal} months {isCurrentTerm && '(your term)'}</td>
                              <td style={{ textAlign: 'right', padding: '14px 12px', color: '#1F2937', fontWeight: isCurrentTerm ? 700 : 400, borderBottom: '1px solid #E5E7EB' }}>${payment.toFixed(0)}</td>
                              <td style={{ textAlign: 'right', padding: '14px 12px', color: '#FF4136', fontWeight: isCurrentTerm ? 700 : 600, borderBottom: '1px solid #E5E7EB' }}>${formatCurrency(totalInterest)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs mt-4" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                    Longer loan terms reduce the monthly payment but increase the total interest paid over time.
                  </p>
                </div>
              )}

              {/* Payment Impact Example - REDESIGNED */}
              {autoLoanResult && (
                <div 
                  style={{ 
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    padding: '16px',
                    boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  }}
                  data-testid="payment-impact-example"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#001F3F' }} strokeWidth={2} />
                    <p className="text-xs font-medium" style={{ color: '#001F3F' }}>Example insight:</p>
                  </div>
                  <p className="text-sm" style={{ color: '#374151', lineHeight: '1.6' }}>
                    Extending a loan from 60 months to 84 months could reduce the monthly payment by over $100 per month, but may increase the total interest by several thousand dollars.
                  </p>
                </div>
              )}

              {/* What Changes Your Payment Most - REDESIGNED as horizontal cards */}
              {autoLoanResult && (
                <div data-testid="payment-factors">
                  <p className="text-sm font-medium mb-3" style={{ color: '#1F2937' }}>Your payment is most affected by:</p>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Vehicle Price Card */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        padding: '16px',
                        boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
                        textAlign: 'center',
                      }}
                    >
                      <div 
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                        }}
                      >
                        <Car className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span style={{ color: '#1F2937', fontWeight: 600, fontSize: '13px' }}>Vehicle Price</span>
                    </div>
                    {/* Interest Rate Card */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        padding: '16px',
                        boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
                        textAlign: 'center',
                      }}
                    >
                      <div 
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                        }}
                      >
                        <Percent className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span style={{ color: '#1F2937', fontWeight: 600, fontSize: '13px' }}>Interest Rate</span>
                    </div>
                    {/* Loan Term Card */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        padding: '16px',
                        boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
                        textAlign: 'center',
                      }}
                    >
                      <div 
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1F2937, #4B5563)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                        }}
                      >
                        <Clock className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span style={{ color: '#1F2937', fontWeight: 600, fontSize: '13px' }}>Loan Term</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Educational Insight */}
              <div 
                style={{ 
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  padding: '16px',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                }}
                data-testid="educational-insight"
              >
                <div className="flex items-start gap-2">
                  <Wallet className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#001F3F' }} strokeWidth={2} />
                  <p className="text-sm" style={{ color: '#374151', lineHeight: '1.6' }}>
                    Many buyers focus only on the monthly payment. The total cost of the loan is what really matters.
                  </p>
                </div>
              </div>

              {/* Primary Button with intro note */}
              {autoLoanResult && (
                <div>
                  <p className="text-sm mb-3 text-center" style={{ color: '#6B7280', fontStyle: 'italic' }}>
                    Got your number? See if it actually fits your life.
                  </p>
                  <a
                    href="/tools/budget"
                    className="w-full flex items-center justify-center gap-2 font-semibold text-base transition-all active:scale-[0.98]"
                    style={{
                      backgroundColor: '#001F3F',
                      color: '#FFFFFF',
                      height: '56px',
                      borderRadius: '20px',
                      boxShadow: '0 6px 20px rgba(0,31,63,0.35)',
                      textDecoration: 'none',
                    }}
                    data-testid="budget-check-btn"
                  >
                    <span>Check if this payment fits my full budget</span>
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </a>
                </div>
              )}

              {/* Disclaimer */}
              <p className="text-xs leading-relaxed" style={{ color: '#9CA3AF' }} data-testid="loan-disclaimer">
                Estimate only. Actual payments may vary based on taxes, dealer fees, timing, and lender terms.
              </p>
            </div>
          )}
        </div>

        {/* 4. Lease Calculator - Charcoal (Budget & costs) */}
        <button
          onClick={() => setShowLeaseCalc(true)}
          className="w-full text-left group"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.budget;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
          data-testid="lease-calculator-btn"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={Calculator} category="budget" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                Lease Calculator – The Real Math
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                Reverse-engineer the payment before you trust it.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </button>

        {/* 5. True Cost to Own - Charcoal (Budget & costs) - COMING SOON */}
        <div
          className="w-full text-left"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            opacity: COMING_SOON_STYLES.opacity,
          }}
          data-testid="true-cost-to-own-card"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={Gauge} category="budget" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy }}>
                  True Cost to Own (5-Year View)
                </h2>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 8px', borderRadius: '4px' }}>COMING SOON</span>
              </div>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                See the real total cost of owning or leasing over 5 years—including depreciation, insurance, fuel, maintenance, and taxes. It ties directly to your budget so you know the true monthly impact, avoid surprises from hidden costs, and can plan with the full picture.
              </p>
            </div>
            <Lock className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </div>

        {/* 6. Depreciation & Resale Simulator - Charcoal (Budget & costs) - COMING SOON */}
        <div
          className="w-full text-left"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            opacity: COMING_SOON_STYLES.opacity,
          }}
          data-testid="depreciation-simulator-card"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={BarChart3} category="budget" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy }}>
                  Depreciation & Resale Simulator
                </h2>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 8px', borderRadius: '4px' }}>COMING SOON</span>
              </div>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                See how much value this car loses over time with a simple curve. Adjust miles per year and see projected trade-in or buyout value. So you understand the biggest hidden cost of any car and make smarter buy/lease/trade decisions. This reveals the true long-term picture.
              </p>
            </div>
            <Lock className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </div>

        {/* SECTION 3: DEALER NEGOTIATION TOOLS */}
        <div style={{ marginTop: '32px', marginBottom: '20px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h2 
            style={{ 
              color: BRAND.navy,
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '6px',
              letterSpacing: '-0.01em',
            }}
          >
            Buying Prep Tools
          </h2>
          <p 
            style={{ 
              color: '#6B7280', 
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            Use these before you compare quotes, talk payments, or sign anything.
          </p>
        </div>

        {/* 1. OTD Quote Emailer - Red (Dealer) */}
        <div 
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onClick={() => navigate('/public-tools/otd-emailer')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.dealer;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
          data-testid="otd-quote-emailer-card"
        >
          <div
            className="w-full text-left group"
            style={{ 
              padding: CARD_STYLES.padding,
              transition: 'background-color 200ms ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <ToolIcon icon={Mail} category="dealer" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                  Out-the-Door Quote Emailer + Phone Script
                </h2>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                  Get the full number in writing before you drive anywhere.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
            </div>
          </div>
        </div>

        {/* 2. APR Comparison - Red (Dealer offers) */}
        <div 
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.dealer;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
        >
          <button
            onClick={() => setActiveCalculator(activeCalculator === 'apr' ? null : 'apr')}
            className="w-full text-left group"
            style={{ 
              padding: CARD_STYLES.padding,
              transition: CARD_STYLES.transition,
              backgroundColor: 'transparent',
            }}
            data-testid="apr-comparison-toggle"
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              {/* Red gradient square icon */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  background: 'linear-gradient(180deg, #CC3333 0%, #FF4136 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Percent className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                  Which Rate Actually Saves Me Money?
                </h2>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                  Compare two APRs and see the real monthly and lifetime difference.
                </p>
              </div>
              {activeCalculator === 'apr' ? (
                <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
              ) : (
                <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
              )}
            </div>
          </button>

          {activeCalculator === 'apr' && (
            <div className="px-5 pb-5 pt-4 border-t space-y-5" style={{ borderTopColor: '#E5E7EB' }}>
              
              {/* Hook callout card */}
              <div 
                style={{ 
                  backgroundColor: '#FFFFFF',
                  borderLeft: '4px solid #001F3F',
                  borderRadius: '0px 8px 8px 0px',
                  boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)',
                  padding: '16px',
                }}
              >
                <p style={{ fontSize: '14px', color: BRAND.charcoal, lineHeight: '1.6', margin: 0 }}>
                  On a $44,000 loan over 72 months, the difference between 5% and 7% APR can cost about $3,000 in extra interest. Two little numbers — thousands of real dollars.
                </p>
              </div>

              {/* Row 1: Loan Amount + Term */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                      Loan Amount
                    </label>
                    <TapToTypeValue
                      value={Number(loanAmount) || 25000}
                      onChange={(val) => setLoanAmount(String(val))}
                      min={5000}
                      max={100000}
                      prefix="$"
                    />
                  </div>
                  <input
                    type="range"
                    min={5000}
                    max={100000}
                    step={1000}
                    value={loanAmount || 25000}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    className="universal-slider w-full"
                    style={{
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(loanAmount || 25000) - 5000) / 95000) * 100}%, #E5E7EB ${((Number(loanAmount || 25000) - 5000) / 95000) * 100}%, #E5E7EB 100%)`,
                    }}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                      Term (months)
                    </label>
                    <TapToTypeValue
                      value={Number(term) || 60}
                      onChange={(val) => setTerm(String(val))}
                      min={12}
                      max={96}
                      suffix=" mo"
                    />
                  </div>
                  <input
                    type="range"
                    min={12}
                    max={96}
                    step={6}
                    value={term || 60}
                    onChange={(e) => setTerm(e.target.value)}
                    className="universal-slider w-full"
                    style={{
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(term || 60) - 12) / 84) * 100}%, #E5E7EB ${((Number(term || 60) - 12) / 84) * 100}%, #E5E7EB 100%)`,
                    }}
                  />
                </div>
              </div>

              {/* Row 2: Dealer Rate + Your Rate */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="text-sm font-semibold" style={{ color: '#FF4136' }}>
                        Dealer Rate (APR)
                      </label>
                      <p className="text-xs" style={{ color: '#6B7280' }}>(what they offered you)</p>
                    </div>
                    <TapToTypeValue
                      value={Number(apr1) || 7}
                      onChange={(val) => setApr1(String(val))}
                      min={0}
                      max={25}
                      suffix="%"
                      decimals={1}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={25}
                    step={0.5}
                    value={apr1 || 7}
                    onChange={(e) => setApr1(e.target.value)}
                    className="universal-slider w-full"
                    style={{
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(apr1 || 7)) / 25) * 100}%, #E5E7EB ${((Number(apr1 || 7)) / 25) * 100}%, #E5E7EB 100%)`,
                    }}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="text-sm font-semibold" style={{ color: '#001F3F' }}>
                        Your Rate (APR)
                      </label>
                      <p className="text-xs" style={{ color: '#6B7280' }}>(credit union or bank pre-approval)</p>
                    </div>
                    <TapToTypeValue
                      value={Number(apr2) || 5}
                      onChange={(val) => setApr2(String(val))}
                      min={0}
                      max={25}
                      suffix="%"
                      decimals={1}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={25}
                    step={0.5}
                    value={apr2 || 5}
                    onChange={(e) => setApr2(e.target.value)}
                    className="universal-slider w-full"
                    style={{
                      background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((Number(apr2 || 5)) / 25) * 100}%, #E5E7EB ${((Number(apr2 || 5)) / 25) * 100}%, #E5E7EB 100%)`,
                    }}
                  />
                </div>
              </div>

              <button
                onClick={calculateAPRSavings}
                className="w-full py-3.5 font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.99]"
                style={{ 
                  backgroundColor: BRAND.navy, 
                  color: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(0,31,63,0.20)',
                  animation: loanAmount && term && apr1 && apr2 ? 'pulse-once 0.6s ease-in-out' : 'none',
                }}
              >
                <span>Compare Rates</span>
                <ArrowRight className="w-4 h-4" style={{ color: BRAND.red }} strokeWidth={2.5} />
              </button>

              {result && (
                <div 
                  className="p-4 rounded-xl"
                  style={{ 
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-5 h-5" style={{ color: BRAND.green }} strokeWidth={2} />
                    <h3 className="font-semibold text-sm" style={{ color: BRAND.navy }}>
                      Rate Comparison
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span style={{ color: '#6B7280' }}>Monthly difference:</span>
                      <span className="font-bold" style={{ color: BRAND.green }}>${result.monthlySavings}/mo</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: '#6B7280' }}>Total interest difference:</span>
                      <span className="font-bold" style={{ color: BRAND.green }}>${result.savings}</span>
                    </div>
                    <div 
                      className="pt-3 mt-3 text-xs leading-relaxed"
                      style={{ borderTop: '1px solid #E5E7EB', color: '#6B7280' }}
                    >
                      Dealer rate payment: ${result.payment1}/mo (${result.interest1} total interest)<br/>
                      Your rate payment: ${result.payment2}/mo (${result.interest2} total interest)
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs leading-relaxed flex items-start gap-2" style={{ color: '#1F2937' }}>
                <Info className="w-4 h-4 flex-shrink-0" style={{ color: BRAND.navy, marginTop: '1px' }} strokeWidth={2} />
                <span><strong>Note:</strong> This is an illustrative calculation. Actual payments may vary based on exact APR, fees, and loan terms.</span>
              </p>
            </div>
          )}
        </div>

        {/* 3. Four Square Simulator - Red (Dealer) */}
        <button
          onClick={() => setShowFourSquare(true)}
          className="w-full text-left group"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.dealer;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
          data-testid="four-square-simulator-btn"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={Grid} category="dealer" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                Four Square Simulator
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                See how price, trade-in, down payment, and monthly payment affect each other.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </button>

        {/* 4. Check My Dealer Lease Quote - Red (Dealer) */}
        <button
          onClick={() => setShowLeaseChecker(true)}
          className="w-full text-left group"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.dealer;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
          data-testid="lease-checker-btn"
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={AlertTriangle} category="dealer" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                Check My Dealer Lease Quote
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                Review the payment, money factor, fees, and cap cost before you sign.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </button>

        {/* 5. Get the Most Money for Your Old Car - Red (Dealer) */}
        <a
          href="/tools/trade-break-even"
          className="block group"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderRadius: CARD_STYLES.borderRadius,
            border: CARD_STYLES.border,
            boxShadow: CARD_STYLES.shadow,
            padding: CARD_STYLES.padding,
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = CARD_STYLES.hoverTransform;
            e.currentTarget.style.boxShadow = HOVER_SHADOWS.dealer;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = CARD_STYLES.shadow;
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <ToolIcon icon={Scale} category="dealer" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: BRAND.navy, marginBottom: '6px' }}>
                Get the Most Money for Your Old Car
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                Compare trade-in tax savings against CarMax, Carvana, or private sale.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#9CA3AF', marginTop: '2px' }} strokeWidth={2} />
          </div>
        </a>
      </div>

      {/* Four Square Simulator Modal */}
      {showFourSquare && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ backgroundColor: '#F8F9FB' }}
        >
          {/* Navigation Bar */}
          <div 
            className="sticky top-0 z-10"
            style={{ 
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E8EAED',
            }}
          >
            <div className="max-w-screen-sm mx-auto px-5 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowFourSquare(false);
                    resetFourSquare();
                  }}
                  className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                  style={{ color: BRAND.navy }}
                  data-testid="four-square-close-btn"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2} />
                  <span className="font-medium">Back</span>
                </button>
                <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
                  VINdicated.<span style={{ color: BRAND.red }}>ai</span>
                </span>
              </div>
            </div>
          </div>

          {/* HERO MODULE - Clean sophisticated header */}
          <div 
            style={{ 
              backgroundColor: '#FFFFFF',
              padding: '32px 0 24px 0',
            }}
          >
            <div className="max-w-screen-sm mx-auto px-5">
              {/* Title with red underline on "Four Square" */}
              <h1 className="text-2xl font-bold tracking-tight mb-3 text-center" style={{ color: '#001F3F', letterSpacing: '-0.5px' }}>
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  Four Square
                  <span style={{ 
                    position: 'absolute', 
                    bottom: '-4px', 
                    left: '-4px', 
                    right: '-4px', 
                    height: '3px', 
                    backgroundColor: '#FF4136', 
                    borderRadius: '2px' 
                  }}></span>
                </span>
                {' '}Simulator
              </h1>
              
              <p className="text-sm leading-relaxed text-center" style={{ color: '#374151', lineHeight: '1.7', maxWidth: '520px', margin: '0 auto' }}>
                Dealers use this worksheet to juggle price, trade-in, down payment, and monthly payment. The payment can look the same while the total cost goes up. This tool shows you exactly what moved.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-screen-sm mx-auto px-5 py-6">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* ============================================ */}
              {/* WHAC-A-MOLE VISUAL CARD - Redesigned */}
              {/* ============================================ */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(0, 31, 63, 0.06)',
                  padding: '24px',
                }}
              >
                {/* Heading */}
                <p className="text-base font-semibold mb-4 text-center" style={{ color: '#001F3F' }}>
                  Think of it like whack-a-mole
                </p>
                
                {/* 2x2 Grid Visual */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                  <div style={{ position: 'relative' }}>
                    {/* The grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 80px', gap: '4px' }}>
                      {/* Price - Top Left - Navy (what buyer focuses on) */}
                      <div 
                        className="four-square-whack-box"
                        style={{ 
                          width: '80px', 
                          height: '60px', 
                          backgroundColor: '#FFFFFF',
                          border: '2px solid #001F3F',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span className="text-xs font-bold" style={{ color: '#001F3F' }}>Price</span>
                        <span style={{ color: '#001F3F', fontSize: '14px', marginTop: '2px' }}>↓</span>
                      </div>
                      
                      {/* Monthly - Top Right - Solid charcoal (the decoy) */}
                      <div 
                        className="four-square-whack-box"
                        style={{ 
                          width: '80px', 
                          height: '60px', 
                          backgroundColor: '#FFFFFF',
                          border: '2px solid #9CA3AF',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span className="text-xs font-bold text-center" style={{ color: '#6B7280', fontSize: '10px', lineHeight: '1.2' }}>Monthly<br/>Payment</span>
                        <span style={{ color: '#6B7280', fontSize: '14px', marginTop: '2px' }}>—</span>
                      </div>
                      
                      {/* Trade - Bottom Left - Red (dealer takes money) */}
                      <div 
                        className="four-square-whack-box"
                        style={{ 
                          width: '80px', 
                          height: '60px', 
                          backgroundColor: '#FFFFFF',
                          border: '2px solid #FF4136',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span className="text-xs font-bold" style={{ color: '#FF4136' }}>Trade-In</span>
                        <span style={{ color: '#FF4136', fontSize: '14px', marginTop: '2px' }}>↓</span>
                      </div>
                      
                      {/* Down - Bottom Right - Red (dealer pushes up) */}
                      <div 
                        className="four-square-whack-box"
                        style={{ 
                          width: '80px', 
                          height: '60px', 
                          backgroundColor: '#FFFFFF',
                          border: '2px solid #FF4136',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span className="text-xs font-bold text-center" style={{ color: '#FF4136', fontSize: '10px', lineHeight: '1.2' }}>Down<br/>Payment</span>
                        <span style={{ color: '#FF4136', fontSize: '14px', marginTop: '2px' }}>↑</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Description text */}
                <p className="text-sm leading-relaxed text-center mb-2" style={{ color: '#6B7280' }}>
                  Push one number down, another pops up. The monthly payment stays the same while the real cost changes.
                </p>
                <p className="text-xs leading-relaxed text-center" style={{ color: '#6B7280' }}>
                  Watch how dealers can keep your monthly payment the same while increasing total interest or add-ons.
                </p>
              </div>

              {/* ============================================ */}
              {/* ACTION SECTION: Your four-square */}
              {/* ============================================ */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '20px' }}>
                  {/* Section Header with Reset Button */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-1">
                      <h2 className="text-base font-bold" style={{ color: BRAND.navy }}>
                        Your four-square
                      </h2>
                      <button
                        onClick={() => setFourSquareInputs({
                          vehiclePrice: 35000,
                          tradeIn: 5000,
                          downPayment: 3000,
                          apr: 7,
                          termMonths: 60,
                          addOns: 0,
                        })}
                        className="text-xs font-medium px-3 py-1.5 transition-colors hover:bg-gray-100"
                        style={{ color: '#001F3F', border: '1px solid #001F3F', borderRadius: '8px', backgroundColor: '#FFFFFF' }}
                      >
                        Reset to Defaults
                      </button>
                    </div>
                    <p className="text-sm" style={{ color: '#6B7280' }}>
                      Adjust the boxes to see how numbers connect.
                    </p>
                  </div>
                  
                  {/* Instructional Note */}
                  <div 
                    className="mb-5 p-4 rounded-xl"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                  >
                    <p className="text-xs leading-relaxed" style={{ color: '#374151' }}>
                      Try this common scenario first — a $35k car with $5k trade + $3k down at 7% APR / 60 months. Slide any control to see how dealers keep payment &apos;the same&apos; while total cost rises.
                    </p>
                  </div>
                  
                  {/* 2x2 Grid of Control Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Price Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                          >
                            <Car className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#374151' }}>Price</span>
                        </div>
                        <TapToTypeValue
                          value={fourSquareInputs.vehiclePrice}
                          onChange={(val) => setFourSquareInputs(prev => ({ ...prev, vehiclePrice: val }))}
                          min={10000}
                          max={100000}
                          prefix="$"
                        />
                      </div>
                      <input
                        type="range"
                        min={10000}
                        max={100000}
                        step={500}
                        value={fourSquareInputs.vehiclePrice}
                        onChange={(e) => setFourSquareInputs(prev => ({ ...prev, vehiclePrice: parseInt(e.target.value) }))}
                        className="universal-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((fourSquareInputs.vehiclePrice - 10000) / 90000) * 100}%, #E5E7EB ${((fourSquareInputs.vehiclePrice - 10000) / 90000) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                    
                    {/* Monthly Payment Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        border: '2px solid #001F3F', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center gap-2.5 mb-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                        >
                          <DollarSign className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <span className="text-sm font-semibold" style={{ color: '#374151' }}>Monthly payment</span>
                      </div>
                      <div 
                        className="text-center py-3 rounded-lg"
                        style={{ backgroundColor: '#F8FAFC' }}
                      >
                        <p className="text-2xl font-bold" style={{ color: BRAND.green }}>
                          ${fourSquareResults.monthlyPayment?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '0'}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>per month</p>
                      </div>
                    </div>
                    
                    {/* Trade-in Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                          >
                            <ArrowLeftRight className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#374151' }}>Trade-in</span>
                        </div>
                        <TapToTypeValue
                          value={fourSquareInputs.tradeIn}
                          onChange={(val) => setFourSquareInputs(prev => ({ ...prev, tradeIn: val }))}
                          min={0}
                          max={20000}
                          prefix="$"
                        />
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={20000}
                        step={500}
                        value={fourSquareInputs.tradeIn}
                        onChange={(e) => setFourSquareInputs(prev => ({ ...prev, tradeIn: parseInt(e.target.value) }))}
                        className="universal-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(fourSquareInputs.tradeIn / 20000) * 100}%, #E5E7EB ${(fourSquareInputs.tradeIn / 20000) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                    
                    {/* Down Payment Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                          >
                            <Wallet className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#374151' }}>Down payment</span>
                        </div>
                        <TapToTypeValue
                          value={fourSquareInputs.downPayment}
                          onChange={(val) => setFourSquareInputs(prev => ({ ...prev, downPayment: val }))}
                          min={0}
                          max={10000}
                          prefix="$"
                        />
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={10000}
                        step={500}
                        value={fourSquareInputs.downPayment}
                        onChange={(e) => setFourSquareInputs(prev => ({ ...prev, downPayment: parseInt(e.target.value) }))}
                        className="universal-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(fourSquareInputs.downPayment / 10000) * 100}%, #E5E7EB ${(fourSquareInputs.downPayment / 10000) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                    
                    {/* APR Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                          >
                            <Percent className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#374151' }}>APR (%)</span>
                        </div>
                        <TapToTypeValue
                          value={fourSquareInputs.apr}
                          onChange={(val) => setFourSquareInputs(prev => ({ ...prev, apr: val }))}
                          min={0}
                          max={25}
                          suffix="%"
                          decimals={1}
                        />
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={25}
                        step={0.5}
                        value={fourSquareInputs.apr}
                        onChange={(e) => setFourSquareInputs(prev => ({ ...prev, apr: parseFloat(e.target.value) }))}
                        className="universal-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(fourSquareInputs.apr / 25) * 100}%, #E5E7EB ${(fourSquareInputs.apr / 25) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                    
                    {/* Term Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                          >
                            <Clock className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#374151' }}>Term (months)</span>
                        </div>
                        <TapToTypeValue
                          value={fourSquareInputs.termMonths}
                          onChange={(val) => setFourSquareInputs(prev => ({ ...prev, termMonths: val }))}
                          min={12}
                          max={96}
                          suffix=" mo"
                        />
                      </div>
                      <input
                        type="range"
                        min={12}
                        max={96}
                        step={6}
                        value={fourSquareInputs.termMonths}
                        onChange={(e) => setFourSquareInputs(prev => ({ ...prev, termMonths: parseInt(e.target.value) }))}
                        className="universal-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((fourSquareInputs.termMonths - 12) / 84) * 100}%, #E5E7EB ${((fourSquareInputs.termMonths - 12) / 84) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                    
                    {/* Add-ons Card */}
                    <div 
                      className="four-square-input-card"
                      style={{ 
                        backgroundColor: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '14px', 
                        padding: '16px',
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                          >
                            <Plus className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#374151' }}>Add-ons ($)</span>
                        </div>
                        <TapToTypeValue
                          value={fourSquareInputs.addOns}
                          onChange={(val) => setFourSquareInputs(prev => ({ ...prev, addOns: val }))}
                          min={0}
                          max={5000}
                          prefix="$"
                        />
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={5000}
                        step={250}
                        value={fourSquareInputs.addOns}
                        onChange={(e) => setFourSquareInputs(prev => ({ ...prev, addOns: parseInt(e.target.value) }))}
                        className="universal-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(fourSquareInputs.addOns / 5000) * 100}%, #E5E7EB ${(fourSquareInputs.addOns / 5000) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Results Dashboard */}
                {!fourSquareResults.error && (
                  <div style={{ padding: '0 24px 24px 24px' }}>
                    <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '0 0 20px 0' }} />
                    <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#64748B', letterSpacing: '0.05em' }}>
                      Results
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className="text-center" 
                        style={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '12px', 
                          padding: '14px 12px',
                          boxShadow: '0 2px 12px rgba(0, 31, 63, 0.08)',
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: '#64748B' }}>Amount financed</p>
                        <p className="text-xl font-bold" style={{ color: BRAND.navy }}>
                          ${fourSquareResults.amountFinanced?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>what you borrow</p>
                      </div>
                      <div 
                        className="text-center" 
                        style={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '12px', 
                          padding: '14px 12px',
                          boxShadow: '0 2px 12px rgba(0, 31, 63, 0.08)',
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: '#64748B' }}>Monthly payment</p>
                        <p className="text-xl font-bold" style={{ color: '#059669' }}>
                          ${fourSquareResults.monthlyPayment?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '0'}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>per month</p>
                      </div>
                      <div 
                        className="text-center" 
                        style={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '12px', 
                          padding: '14px 12px',
                          boxShadow: '0 2px 12px rgba(0, 31, 63, 0.08)',
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: '#64748B' }}>Total paid over term</p>
                        <p className="text-xl font-bold" style={{ color: '#374151' }}>
                          ${fourSquareResults.totalPaid?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{fourSquareInputs.termMonths} months</p>
                      </div>
                      <div 
                        className="text-center" 
                        style={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '12px', 
                          padding: '14px 12px',
                          boxShadow: '0 2px 12px rgba(0, 31, 63, 0.08)',
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: '#64748B' }}>Total interest</p>
                        <p className="text-xl font-bold" style={{ color: '#DC2626' }}>
                          ${fourSquareResults.totalInterest?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>cost of borrowing</p>
                      </div>
                    </div>
                    
                    {/* Dynamic Insight Card - Compare current interest to initial */}
                    {fourSquareInitialInterestRef.current !== null && fourSquareResults.totalInterest !== fourSquareInitialInterestRef.current && (
                      (() => {
                        const diff = fourSquareResults.totalInterest - fourSquareInitialInterestRef.current;
                        const absDiff = Math.abs(diff);
                        
                        // Only show if difference is more than $50
                        if (absDiff <= 50) {
                          return (
                            <div 
                              className="mt-4"
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #1F2937',
                                borderRadius: '12px',
                                padding: '16px',
                                boxShadow: '0 2px 12px rgba(31,41,55,0.10)',
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <Info className="w-6 h-6 flex-shrink-0" style={{ color: '#1F2937' }} />
                                <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>
                                  The total interest barely changed — but check what moved. Did the price drop, or did the dealer just shift numbers around?
                                </p>
                              </div>
                            </div>
                          );
                        }
                        
                        if (diff > 50) {
                          return (
                            <div 
                              className="mt-4"
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #FF4136',
                                borderRadius: '12px',
                                padding: '16px',
                                boxShadow: '0 2px 12px rgba(255,65,54,0.10)',
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: '#FF4136' }} />
                                <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>
                                  The numbers shifted — but you&apos;re now paying <span className="font-bold" style={{ color: '#FF4136' }}>${absDiff.toLocaleString('en-US', { maximumFractionDigits: 0 })} MORE</span> in total interest. That&apos;s the four-square at work. A dealer can make the payment look better while your real cost goes up.
                                </p>
                              </div>
                            </div>
                          );
                        }
                        
                        if (diff < -50) {
                          return (
                            <div 
                              className="mt-4"
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #001F3F',
                                borderRadius: '12px',
                                padding: '16px',
                                boxShadow: '0 2px 12px rgba(0,31,63,0.10)',
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <CheckCircle className="w-6 h-6 flex-shrink-0" style={{ color: '#001F3F' }} />
                                <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>
                                  This change actually saves you <span className="font-bold" style={{ color: '#001F3F' }}>${absDiff.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span> in total interest. That&apos;s a real improvement — not just a payment trick.
                                </p>
                              </div>
                            </div>
                          );
                        }
                        
                        return null;
                      })()
                    )}
                  </div>
                )}
              </div>

              {/* ============================================ */}
              {/* COMMON DEALER MOVES - Interactive Scenarios */}
              {/* ============================================ */}
              {!fourSquareResults.error && (
                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid rgba(0,31,63,0.08)',
                    boxShadow: '0 2px 8px rgba(0,31,63,0.04)',
                    overflow: 'hidden',
                  }}
                >
                  <div className="p-5">
                    <h3 className="text-sm font-bold mb-1" style={{ color: BRAND.navy }}>
                      Common dealer moves
                    </h3>
                    <p className="text-xs mb-4" style={{ color: '#6B7280' }}>
                      Tap a move to see what changes — even when the monthly payment looks &apos;fine.&apos;
                    </p>
                    
                    <div className="flex flex-col gap-3 mb-4">
                      <div className="flex flex-col">
                        <button
                          onClick={() => applyScenario('lowerPayment')}
                          className="px-3 py-2 text-xs font-semibold transition-all flex items-center gap-1.5 hover:shadow-md active:scale-[0.98] self-start"
                          style={{ 
                            backgroundColor: activeScenario === 'lowerPayment' ? BRAND.navy : '#FFFFFF',
                            color: activeScenario === 'lowerPayment' ? '#FFFFFF' : BRAND.navy,
                            border: `1px solid ${activeScenario === 'lowerPayment' ? BRAND.navy : '#E5E7EB'}`,
                            borderRadius: '8px',
                          }}
                        >
                          Lower payment trap
                          <ChevronRight className="w-3.5 h-3.5" style={{ color: activeScenario === 'lowerPayment' ? '#FFFFFF' : BRAND.red }} />
                        </button>
                        <p className="text-xs mt-1 ml-1" style={{ color: '#9CA3AF' }}>Try: stretch term to 84 months — payment stays ~$535 but interest jumps $2,000+</p>
                      </div>
                      <div className="flex flex-col">
                        <button
                          onClick={() => applyScenario('addOnBundle')}
                          className="px-3 py-2 text-xs font-semibold transition-all flex items-center gap-1.5 hover:shadow-md active:scale-[0.98] self-start"
                          style={{ 
                            backgroundColor: activeScenario === 'addOnBundle' ? BRAND.navy : '#FFFFFF',
                            color: activeScenario === 'addOnBundle' ? '#FFFFFF' : BRAND.navy,
                            border: `1px solid ${activeScenario === 'addOnBundle' ? BRAND.navy : '#E5E7EB'}`,
                            borderRadius: '8px',
                          }}
                        >
                          Add-on bundle
                          <ChevronRight className="w-3.5 h-3.5" style={{ color: activeScenario === 'addOnBundle' ? '#FFFFFF' : BRAND.red }} />
                        </button>
                        <p className="text-xs mt-1 ml-1" style={{ color: '#9CA3AF' }}>Try: add $2,500–$3,000 in extras — financed amount rises, payment may stay flat</p>
                      </div>
                      <div className="flex flex-col">
                        <button
                          onClick={() => applyScenario('tradeDownshift')}
                          className="px-3 py-2 text-xs font-semibold transition-all flex items-center gap-1.5 hover:shadow-md active:scale-[0.98] self-start"
                          style={{ 
                            backgroundColor: activeScenario === 'tradeDownshift' ? BRAND.navy : '#FFFFFF',
                            color: activeScenario === 'tradeDownshift' ? '#FFFFFF' : BRAND.navy,
                            border: `1px solid ${activeScenario === 'tradeDownshift' ? BRAND.navy : '#E5E7EB'}`,
                            borderRadius: '8px',
                          }}
                        >
                          Trade downshift
                          <ChevronRight className="w-3.5 h-3.5" style={{ color: activeScenario === 'tradeDownshift' ? '#FFFFFF' : BRAND.red }} />
                        </button>
                        <p className="text-xs mt-1 ml-1" style={{ color: '#9CA3AF' }}>Try: lower trade allowance $1,000–$2,000 — more financed, higher total cost</p>
                      </div>
                    </div>

                    {/* What changed callout */}
                    {fourSquareBeforeSnapshot && activeScenario && whatChanged && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div 
                          style={{ 
                            backgroundColor: fourSquareResults.totalPaid > fourSquareBeforeSnapshot.results.totalPaid ? '#FEF2F2' : '#F8FAFC',
                            border: fourSquareResults.totalPaid > fourSquareBeforeSnapshot.results.totalPaid ? '1px solid #FECACA' : '1px solid #E5E7EB',
                            borderRadius: '10px',
                            padding: '14px',
                          }}
                        >
                          <h4 className="text-xs font-bold mb-2" style={{ color: BRAND.navy }}>What changed</h4>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {whatChanged.changes.map((change, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <div className="flex-shrink-0" style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: BRAND.navy }} />
                                <p className="text-xs" style={{ color: '#374151' }}>
                                  <span className="font-medium">{change.label}:</span> {change.before} → {change.after}
                                </p>
                              </div>
                            ))}
                            
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0" style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: fourSquareResults.totalPaid > fourSquareBeforeSnapshot.results.totalPaid ? '#DC2626' : '#16A34A' }} />
                              <p className="text-xs" style={{ color: fourSquareResults.totalPaid > fourSquareBeforeSnapshot.results.totalPaid ? '#DC2626' : '#16A34A' }}>
                                <span className="font-medium">Total paid:</span> {fourSquareResults.totalPaid > fourSquareBeforeSnapshot.results.totalPaid ? '+' : '-'}${Math.abs(fourSquareResults.totalPaid - fourSquareBeforeSnapshot.results.totalPaid).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0" style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: fourSquareResults.totalInterest > fourSquareBeforeSnapshot.results.totalInterest ? '#DC2626' : '#16A34A' }} />
                              <p className="text-xs" style={{ color: fourSquareResults.totalInterest > fourSquareBeforeSnapshot.results.totalInterest ? '#DC2626' : '#16A34A' }}>
                                <span className="font-medium">Total interest:</span> {fourSquareResults.totalInterest > fourSquareBeforeSnapshot.results.totalInterest ? '+' : '-'}${Math.abs(fourSquareResults.totalInterest - fourSquareBeforeSnapshot.results.totalInterest).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              </p>
                            </div>
                          </div>
                          
                          <p className="text-xs mt-3 pt-2 leading-relaxed" style={{ borderTop: '1px solid #F3F4F6', color: '#6B7280', fontStyle: 'italic' }}>
                            Like forgetting to add tax - the payment looks good, but total swells.
                          </p>
                        </div>

                        <button
                          onClick={resetFourSquare}
                          className="w-full py-2.5 text-xs font-semibold transition-colors hover:bg-gray-100"
                          style={{ backgroundColor: '#F3F4F6', color: BRAND.navy, borderRadius: '10px' }}
                        >
                          Reset to defaults
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ============================================ */}
              {/* HOW TO STOP FOUR-SQUARE GAMES */}
              {/* ============================================ */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,31,63,0.08)',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.04)',
                  overflow: 'hidden',
                }}
              >
                <div className="p-5">
                  <h3 className="text-sm font-bold mb-4" style={{ color: BRAND.navy }}>
                    How to stop four-square games
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Tip 1 */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <Target className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          Negotiate the out-the-door total first. Not the monthly payment, not the price alone — the full OTD number with taxes, fees, and add-ons included.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tip 2 */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <DollarSign className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          Treat the monthly payment as a result — not the goal. The payment is just math based on price, term, rate, and down payment. If the dealer starts with &quot;What monthly payment works for you?&quot; — redirect to the total price.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tip 3 */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <MessageCircle className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          If the payment changes, ask exactly what changed. Did the price drop? Did the term stretch? Did the rate go up? Did they remove your trade-in value? Something moved — find out what.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tip 4 - NEW */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <List className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          Never negotiate all four numbers at the same time. That&apos;s exactly how the four-square works — the dealer juggles price, trade, down payment, and financing simultaneously so you can&apos;t track what&apos;s moving. Handle each one separately. Price first. Then trade-in. Then financing. One at a time.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tip 5 - NEW */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <FileText className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          Ask to see each line item separately. This is the most powerful sentence you can say at a dealership: &quot;I&apos;d like to see the price, trade-in value, and financing broken out individually — not combined.&quot; It kills the four-square instantly because it forces transparency.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tip 6 - NEW */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <Clock className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          Watch out for the term stretch. A dealer can drop your monthly payment by stretching a 60-month loan to 72 or 84 months. The payment looks lower but you pay thousands more in interest — and on a depreciating car, you could be underwater for years. Always compare total cost, not monthly cost.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tip 7 - NEW */}
                    <div 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                          }}
                        >
                          <PlusCircle className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          The add-ons play is the final four-square move. After you agree on a payment, the finance office adds $2,000 to $3,000 in extras — paint protection, extended warranty, GAP insurance. The monthly barely moves because they quietly stretched the term. By the time you notice, you&apos;ve already signed. This is why you negotiate the OTD total first and get it in writing before you ever sit in the finance office.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Disclaimer */}
              <div className="pt-2 pb-6">
                <p className="text-xs text-center leading-relaxed mb-2" style={{ color: '#6B7280' }}>
                  Dealers focus on monthly payment because it feels affordable — always negotiate the out-the-door total first (see OTD Emailer tool).
                </p>
                <p className="text-xs text-center leading-relaxed" style={{ color: '#9CA3AF' }}>
                  Educational use only. Not financial, legal, or tax advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy or Lease Quiz Modal */}
      {showQuiz && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ backgroundColor: '#F8F9FB' }}
          data-testid="quiz-modal"
        >
          {/* Navigation Bar */}
          <div 
            className="sticky top-0 z-10"
            style={{ 
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E8EAED',
            }}
          >
            <div className="max-w-screen-sm mx-auto px-5 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowQuiz(false);
                    resetQuiz();
                  }}
                  className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                  style={{ color: BRAND.navy }}
                  data-testid="quiz-close-btn"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2} />
                  <span className="font-medium">Back</span>
                </button>
                <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
                  VINdicated.<span style={{ color: BRAND.red }}>ai</span>
                </span>
              </div>
            </div>
          </div>

          {/* Quiz Intro Screen - Streamlined Version with Energy */}
          {quizStep === 'intro' && (
            <div className="max-w-screen-sm mx-auto px-5" style={{ backgroundColor: '#FFFFFF' }}>
              
              {/* Progress Bar at Top - 4% to show they've already started */}
              <div className="py-4">
                <div className="flex items-center justify-end mb-2">
                  <span style={{ color: '#001F3F', fontWeight: 700, fontSize: '13px' }}>4%</span>
                </div>
                <div 
                  style={{ 
                    height: '4px', 
                    backgroundColor: '#E5E7EB', 
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}
                >
                  <div 
                    style={{ 
                      width: '4%',
                      height: '100%',
                      backgroundColor: '#001F3F',
                      borderRadius: '2px',
                    }}
                  />
                </div>
              </div>

              {/* 1. TOP HERO SECTION */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h1 
                  className="text-2xl font-bold mb-4" 
                  style={{ color: '#1F2937', letterSpacing: '-0.3px' }}
                  data-testid="quiz-intro-title"
                >
                  New, Used, or Lease?<br/>Find what actually fits your life.
                </h1>
                {/* Emotional Hook */}
                <p 
                  className="text-sm leading-relaxed" 
                  style={{ color: '#4B5563', lineHeight: '1.7' }}
                >
                  The wrong choice here can cost you thousands over the life of the loan — or lock you into something that doesn&apos;t fit. This takes 2 minutes to get right.
                </p>
              </div>

              {/* Quote Block - Clean white card */}
              <div 
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '32px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                }}
              >
                <p className="text-sm" style={{ color: '#374151', lineHeight: '1.6', fontStyle: 'italic' }}>
                  &quot;Some drivers should never lease. Others should almost always lease. This quiz helps you find out which side you&apos;re on.&quot;
                </p>
              </div>

              {/* 2. THREE PREMIUM ICON CARDS WITH BENEFIT LINES */}
              <div 
                className="grid grid-cols-1 sm:grid-cols-3"
                style={{ gap: '16px', marginBottom: '24px' }}
              >
                {/* NEW Card */}
                <div 
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '20px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    padding: '28px 20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 31, 63, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Navy Gradient Car Icon */}
                  <div 
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 28L12 18C12.5 16.5 14 15 16 15H32C34 15 35.5 16.5 36 18L40 28" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 28H42V34C42 35.1 41.1 36 40 36H8C6.9 36 6 35.1 6 34V28Z" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="13" cy="32" r="3" stroke="#FFFFFF" strokeWidth="2" fill="none"/>
                      <circle cx="35" cy="32" r="3" stroke="#FFFFFF" strokeWidth="2" fill="none"/>
                      <path d="M16 22H32" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p style={{ fontSize: '17px', fontWeight: 700, color: '#1F2937', marginBottom: '10px' }}>New</p>
                  <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.55', textAlign: 'center' }}>
                    Best if you plan to keep it long-term and want full warranty coverage
                  </p>
                </div>

                {/* USED Card */}
                <div 
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '20px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    padding: '28px 20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(31, 41, 55, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Charcoal Gradient Car Icon */}
                  <div 
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1F2937, #4B5563)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 28L12 18C12.5 16.5 14 15 16 15H32C34 15 35.5 16.5 36 18L40 28" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 28H42V34C42 35.1 41.1 36 40 36H8C6.9 36 6 35.1 6 34V28Z" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="13" cy="32" r="3" stroke="#FFFFFF" strokeWidth="2" fill="none"/>
                      <circle cx="35" cy="32" r="3" stroke="#FFFFFF" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <p style={{ fontSize: '17px', fontWeight: 700, color: '#1F2937', marginBottom: '10px' }}>Used</p>
                  <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.55', textAlign: 'center' }}>
                    Best if you want the most car for your money and don&apos;t mind a little homework
                  </p>
                </div>

                {/* LEASE Card */}
                <div 
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '20px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    padding: '28px 20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 65, 54, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Red Gradient Document Icon */}
                  <div 
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <FileText className="w-7 h-7" style={{ color: '#FFFFFF' }} strokeWidth={1.5} />
                  </div>
                  <p style={{ fontSize: '17px', fontWeight: 700, color: '#1F2937', marginBottom: '10px' }}>Lease</p>
                  <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.55', textAlign: 'center' }}>
                    Best if you like driving new every few years and want a predictable payment
                  </p>
                </div>
              </div>

              {/* Not Sure Note */}
              <p style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '20px', fontStyle: 'italic', textAlign: 'center' }}>
                Not sure? That&apos;s exactly why this exists.
              </p>

              {/* START BUTTON */}
              <button
                onClick={() => setQuizStep('questions')}
                className="w-full py-4 px-4 text-base font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                style={{
                  backgroundColor: '#001F3F',
                  color: '#FFFFFF',
                  borderRadius: '14px',
                  boxShadow: '0 4px 12px rgba(0, 31, 63, 0.25)',
                  marginBottom: '12px',
                }}
                data-testid="quiz-start-btn"
              >
                <span>Find My Best Option</span>
                <ArrowRight className="w-4 h-4" style={{ color: '#FF4136' }} strokeWidth={2.5} />
              </button>

              {/* Privacy Note - directly under button */}
              <p style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', marginBottom: '20px' }}>
                No email required. Your answers stay on your device.
              </p>

              {/* FOOTER DISCLAIMER */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Info className="w-4 h-4" style={{ color: '#9CA3AF' }} strokeWidth={2} />
                <p className="text-xs" style={{ color: '#9CA3AF' }}>
                  Education-only guidance — not financial or legal advice.
                </p>
              </div>
            </div>
          )}

          {/* Quiz Questions Screen */}
          {quizStep === 'questions' && (
            <div>
              {/* Progress Header - percentage only, no question count */}
              <div className="bg-white border-b" style={{ borderBottomColor: '#E5E7EB' }}>
                <div className="max-w-screen-sm mx-auto px-5 py-4">
                  <div className="flex items-center justify-end mb-2">
                    <span style={{ color: '#001F3F', fontWeight: 700, fontSize: '13px' }}>
                      {Math.round(((currentQuestionIndex + 1) / quizQuestions.length) * 100)}%
                    </span>
                  </div>
                  <div 
                    style={{ 
                      height: '4px', 
                      backgroundColor: '#E5E7EB', 
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}
                  >
                    <div 
                      style={{ 
                        width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%`,
                        height: '100%',
                        backgroundColor: '#001F3F',
                        borderRadius: '2px',
                        transition: 'width 300ms ease-out',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Question */}
              <div className="max-w-screen-sm mx-auto px-5 py-5">
                {/* Engagement Messages - EXACT per spec */}
                {currentQuestionIndex === 4 && (
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px', marginBottom: '16px' }}>
                    <p className="text-xs text-center" style={{ color: '#10B981', fontWeight: 500 }}>Nice. You're making great progress.</p>
                  </div>
                )}
                {currentQuestionIndex === 9 && (
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px', marginBottom: '16px' }}>
                    <p className="text-xs text-center" style={{ color: '#10B981', fontWeight: 500 }}>Great — just a few more questions.</p>
                  </div>
                )}
                {currentQuestionIndex === 13 && (
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px', marginBottom: '16px' }}>
                    <p className="text-xs text-center" style={{ color: '#10B981', fontWeight: 500 }}>Halfway there.</p>
                  </div>
                )}
                {currentQuestionIndex === 19 && (
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px', marginBottom: '16px' }}>
                    <p className="text-xs text-center" style={{ color: '#10B981', fontWeight: 500 }}>Almost done.</p>
                  </div>
                )}
                {currentQuestionIndex === 25 && (
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px', marginBottom: '16px' }}>
                    <p className="text-xs text-center" style={{ color: '#001F3F', fontWeight: 600 }}>One last question before we show your result.</p>
                  </div>
                )}

                <h2 
                  className="text-lg font-semibold mb-4" 
                  style={{ color: '#001F3F', lineHeight: '1.4' }}
                  data-testid="quiz-question-title"
                >
                  {/* Override Q1 title for clarity */}
                  {currentQuestionIndex === 0 
                    ? "How long do you expect to keep your next car?"
                    : quizQuestions[currentQuestionIndex].title.replace(/^Q\d+\s*-\s*/, '')
                  }
                </h2>

                {/* Answer Options with checkmark */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="quiz-answer-options">
                  {quizQuestions[currentQuestionIndex].answers.map((answer) => {
                    const isSelected = quizAnswers[quizQuestions[currentQuestionIndex].id] === answer.label;
                    return (
                      <button
                        key={answer.label}
                        onClick={() => handleQuizAnswerSelect(answer)}
                        className="w-full text-left py-3.5 px-4 transition-all duration-150 active:scale-[0.98]"
                        style={{
                          backgroundColor: isSelected ? 'rgba(0, 31, 63, 0.06)' : '#FFFFFF',
                          border: isSelected ? '2px solid #001F3F' : '1px solid #E5E7EB',
                          borderRadius: '12px',
                          boxShadow: isSelected ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.04)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        data-testid={`quiz-answer-${answer.label.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                      >
                        <span 
                          className="text-base"
                          style={{ 
                            color: isSelected ? '#001F3F' : '#374151',
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          {answer.label}
                        </span>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5" style={{ color: '#001F3F' }} strokeWidth={2} />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Navigation - Back button only, auto-advance handles Next */}
                <div className="mt-6" style={{ display: 'flex', gap: '12px' }}>
                  {currentQuestionIndex > 0 && (
                    <button
                      onClick={handleQuizBack}
                      className="py-3 px-4 text-sm font-medium transition-all duration-150"
                      style={{
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        borderRadius: '12px',
                      }}
                      data-testid="quiz-back-btn"
                    >
                      Back
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Quiz Results Screen - Per Master Spec */}
          {quizStep === 'results' && (
            <div className="max-w-screen-sm mx-auto px-5 py-6">
              {(() => {
                const winner = getQuizWinner();
                const winnerColor = winner === 'New' ? '#001F3F' : winner === 'Used' ? '#10B981' : '#3B82F6';
                const archetype = getArchetype(winner, quizScores);
                const bestMatchLabel = winner === 'New' ? 'New Car Purchase' : winner === 'Used' ? 'Used Car Ownership' : 'Lease';
                
                // Get "Why this fits you" bullets based on winner
                const getWhyBullets = () => {
                  if (winner === 'Used') {
                    return [
                      'You keep vehicles longer than average',
                      'You drive higher mileage',
                      'You prioritize long-term value'
                    ];
                  } else if (winner === 'Lease') {
                    return [
                      'You prefer upgrading regularly',
                      'You value predictable payments',
                      'You prioritize flexibility'
                    ];
                  } else {
                    return [
                      'You value warranty coverage',
                      'You want the latest features',
                      'You prefer reliability guarantees'
                    ];
                  }
                };

                return (
                  <>
                    {/* Main Result Card */}
                    <div 
                      className="mb-5 p-6 text-center"
                      style={{
                        backgroundColor: winnerColor,
                        borderRadius: '16px',
                      }}
                      data-testid="quiz-winner-card"
                    >
                      <p className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Your VINdicated Car Fit
                      </p>
                      <h2 className="text-2xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
                        {archetype}
                      </h2>
                      <p className="text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        Best Match: <strong>{bestMatchLabel}</strong>
                      </p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Confidence: <strong>Strong</strong>
                      </p>
                    </div>

                    {/* Why this fits you */}
                    <div 
                      className="mb-5 p-5"
                      style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '14px',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <h3 className="text-sm font-semibold mb-3" style={{ color: '#001F3F' }}>
                        Why this fits you
                      </h3>
                      <ul style={{ paddingLeft: '16px', margin: 0 }}>
                        {getWhyBullets().map((bullet, idx) => (
                          <li key={idx} className="text-sm mb-1" style={{ color: '#4B5563' }}>• {bullet}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Score Breakdown */}
                    <div 
                      className="mb-5 p-5"
                      style={{
                        backgroundColor: '#F9FAFB',
                        borderRadius: '14px',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <h3 className="text-sm font-semibold mb-3" style={{ color: '#001F3F' }}>
                        Score Breakdown
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="flex justify-between">
                          <span className="text-sm" style={{ color: '#4B5563' }}>Used:</span>
                          <span className="text-sm font-bold" style={{ color: '#10B981' }}>{quizScores.U}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm" style={{ color: '#4B5563' }}>Lease:</span>
                          <span className="text-sm font-bold" style={{ color: '#3B82F6' }}>{quizScores.L}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm" style={{ color: '#4B5563' }}>New:</span>
                          <span className="text-sm font-bold" style={{ color: '#001F3F' }}>{quizScores.N}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                      <button
                        onClick={() => {
                          setShowQuiz(false);
                          resetQuiz();
                          navigate('/tools/compare-cars');
                        }}
                        className="w-full py-3.5 px-4 text-base font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: '#001F3F',
                          color: '#FFFFFF',
                          borderRadius: '14px',
                        }}
                        data-testid="quiz-compare-cars-btn"
                      >
                        <span>Rank the Cars Before You Decide</span>
                      </button>
                      <button
                        onClick={resetQuiz}
                        className="w-full py-3 text-sm font-medium transition-all duration-150"
                        style={{
                          backgroundColor: '#F3F4F6',
                          color: '#374151',
                          borderRadius: '12px',
                        }}
                        data-testid="quiz-reset-btn"
                      >
                        Take Quiz Again
                      </button>
                    </div>

                    {/* Result Explanation Toggle */}
                    <div 
                      className="mb-5 p-4"
                      style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '14px',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <button
                        onClick={() => setShowFullPlan(!showFullPlan)}
                        className="w-full text-left flex items-center justify-between"
                      >
                        <span className="text-sm font-semibold" style={{ color: '#001F3F' }}>
                          Why did I get this result?
                        </span>
                        {showFullPlan ? (
                          <ChevronUp className="w-4 h-4" style={{ color: '#6B7280' }} />
                        ) : (
                          <ChevronDown className="w-4 h-4" style={{ color: '#6B7280' }} />
                        )}
                      </button>
                      {showFullPlan && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #E5E7EB' }}>
                          <p className="text-sm mb-2" style={{ color: '#4B5563' }}>Your answers suggest:</p>
                          <ul style={{ paddingLeft: '16px', margin: 0 }}>
                            <li className="text-sm mb-1" style={{ color: '#6B7280' }}>• {winner === 'Used' ? 'long ownership horizon' : winner === 'Lease' ? 'preference for flexibility' : 'desire for latest features'}</li>
                            <li className="text-sm mb-1" style={{ color: '#6B7280' }}>• {winner === 'Used' ? 'comfort with used vehicles' : winner === 'Lease' ? 'focus on monthly payments' : 'value warranty protection'}</li>
                            <li className="text-sm mb-1" style={{ color: '#6B7280' }}>• {winner === 'Used' ? 'focus on total cost savings' : winner === 'Lease' ? 'regular upgrade preference' : 'appreciation for reliability'}</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Debate Hook */}
                    <div 
                      className="mb-5 p-5 text-center"
                      style={{
                        backgroundColor: '#F0F4F8',
                        borderRadius: '14px',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <p className="text-sm font-semibold mb-4" style={{ color: '#001F3F' }}>
                        Do you agree with this result?
                      </p>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <button
                          onClick={() => setDebateResponse('yes')}
                          className="py-2.5 px-6 text-sm font-medium transition-all duration-150 active:scale-[0.98]"
                          style={{
                            backgroundColor: debateResponse === 'yes' ? '#10B981' : '#FFFFFF',
                            color: debateResponse === 'yes' ? '#FFFFFF' : '#374151',
                            border: '1px solid #E5E7EB',
                            borderRadius: '10px',
                          }}
                        >
                          👍 Yes, that's me
                        </button>
                        <button
                          onClick={() => setDebateResponse('unsure')}
                          className="py-2.5 px-6 text-sm font-medium transition-all duration-150 active:scale-[0.98]"
                          style={{
                            backgroundColor: debateResponse === 'unsure' ? '#F59E0B' : '#FFFFFF',
                            color: debateResponse === 'unsure' ? '#FFFFFF' : '#374151',
                            border: '1px solid #E5E7EB',
                            borderRadius: '10px',
                          }}
                        >
                          🤔 Not sure
                        </button>
                      </div>
                      {debateResponse === 'unsure' && (
                        <div className="mt-4 p-3" style={{ backgroundColor: '#FFFFFF', borderRadius: '10px' }}>
                          <p className="text-sm" style={{ color: '#4B5563' }}>
                            Interesting. Many drivers with similar answers still debate this.
                          </p>
                          <p className="text-sm mt-2" style={{ color: '#001F3F', fontWeight: 500 }}>
                            Share your result and see what others think.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Compare with friends */}
                    <div 
                      className="mb-5 p-4 text-center"
                      style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '14px',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <p className="text-sm font-semibold mb-3" style={{ color: '#001F3F' }}>
                        Compare with friends
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => {
                            if (navigator.share) {
                              navigator.share({
                                title: 'My VINdicated Car Fit Result',
                                text: `I'm ${archetype}! Best Match: ${bestMatchLabel}. Find yours at VINdicated.ai`,
                                url: window.location.origin + '/public-tools'
                              });
                            } else {
                              alert('Sharing not supported on this device');
                            }
                          }}
                          className="flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: '#001F3F',
                            color: '#FFFFFF',
                            borderRadius: '10px',
                          }}
                          data-testid="quiz-share-btn"
                        >
                          <Share2 className="w-4 h-4" />
                          <span>Share result</span>
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(window.location.origin + '/public-tools');
                            alert('Quiz link copied!');
                          }}
                          className="flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            borderRadius: '10px',
                          }}
                          data-testid="quiz-copy-link-btn"
                        >
                          <Copy className="w-4 h-4" />
                          <span>Copy quiz link</span>
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Lease Calculator Modal */}
      {showLeaseCalc && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ backgroundColor: '#FFFFFF' }}
          data-testid="lease-calc-modal"
        >
          {/* Tooltip Modal Overlay */}
          {activeTooltip && leaseTooltips[activeTooltip] && (
            <div 
              className="fixed inset-0 z-[60] flex items-center justify-center px-5"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              onClick={() => setActiveTooltip(null)}
            >
              <div 
                className="w-full max-w-md rounded-2xl p-6 relative"
                style={{ 
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                }}
                onClick={(e) => e.stopPropagation()}
                data-testid="tooltip-modal"
              >
                <button
                  onClick={() => setActiveTooltip(null)}
                  className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                  data-testid="close-tooltip-modal"
                >
                  <X className="w-5 h-5" style={{ color: '#6B7280' }} />
                </button>
                <div className="flex items-start gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <Info className="w-5 h-5" style={{ color: '#3B82F6' }} />
                  </div>
                  <h3 className="text-lg font-bold pt-1.5" style={{ color: BRAND.navy }}>
                    {leaseTooltips[activeTooltip].title}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#4B5563', lineHeight: '1.7' }}>
                  {leaseTooltips[activeTooltip].text}
                </p>
                <button
                  onClick={() => setActiveTooltip(null)}
                  className="w-full mt-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                  style={{ backgroundColor: BRAND.navy, color: '#FFFFFF' }}
                >
                  Got it
                </button>
              </div>
            </div>
          )}

          {/* Navigation Bar */}
          <div 
            className="sticky top-0 z-10"
            style={{ 
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E8EAED',
            }}
          >
            <div className="max-w-screen-sm mx-auto px-5 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowLeaseCalc(false)}
                  className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                  style={{ color: BRAND.navy }}
                  data-testid="lease-calc-back-btn"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2} />
                  <span className="font-medium">Back</span>
                </button>
                <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
                  VINdicated.<span style={{ color: BRAND.red }}>ai</span>
                </span>
              </div>
            </div>
            
            {/* Sticky Payment + Deal Score Section */}
            <div 
              style={{ 
                backgroundColor: '#FFFFFF',
                borderTop: '1px solid #E8EAED',
                padding: '16px 0',
              }}
            >
              <div className="max-w-screen-sm mx-auto px-5">
                <div className="flex items-center justify-between gap-4">
                  {/* Final Monthly Payment */}
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide" style={{ color: '#6B7280' }}>Total Monthly</p>
                      <p className="text-2xl font-black" style={{ color: '#001F3F', letterSpacing: '-1px' }}>
                        ${leaseResults.totalMonthlyPayment.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Deal Score Badge - No fill, outline only */}
                  {leaseScore && (
                    <div 
                      className="flex items-center gap-2 px-4 py-2 rounded-full"
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        border: `2px solid ${leaseInputs.moneyFactor > 0.00180 ? '#FF4136' : leaseScore.borderColor}`,
                      }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: leaseInputs.moneyFactor > 0.00180 ? '#FF4136' : leaseScore.color }}
                      ></div>
                      <span className="text-sm font-semibold" style={{ color: leaseInputs.moneyFactor > 0.00180 ? '#FF4136' : leaseScore.color }}>
                        {leaseScore.ratio.toFixed(2)}% – {leaseInputs.moneyFactor > 0.00180 ? 'Caution' : leaseScore.tier}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ======================= */}
          {/* EDUCATION HERO SECTION */}
          {/* SAVED AS: Lease-Calculator-Full-Refresh-HomepageMatch-v1 */}
          {/* ======================= */}
          <div style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
            <div className="max-w-screen-sm mx-auto px-5 py-16">
              {/* Hero Gradient Banner */}
              <div 
                style={{ 
                  background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                  borderRadius: '20px',
                  padding: '28px 24px',
                  marginBottom: '20px',
                }}
              >
                <h1 
                  className="font-bold mb-3" 
                  style={{ color: '#FFFFFF', fontSize: '22px' }}
                >
                  Lease Calculator – The Real Math
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.7' }}>
                  A lease is like renting a car for a fixed time. You only pay for the part you use up plus the cost of borrowing the money. This tool shows you exactly how it all works so you can understand and negotiate with confidence.
                </p>
              </div>

              <h2 className="text-lg font-bold mb-6" style={{ color: '#1F2937' }}>
                How Lease Payments Really Work
              </h2>
              <p className="text-sm leading-relaxed mb-8" style={{ color: '#1F2937', lineHeight: '1.85' }}>
                Your monthly lease payment has three main pieces:
              </p>
              {/* 3-piece explanation cards - REDESIGNED with brand colors */}
              <div 
                className="grid grid-cols-1 sm:grid-cols-3"
                style={{ gap: '24px', marginBottom: '40px' }}
              >
                {/* Selling Price Card - Navy gradient circle */}
                <div 
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
                    padding: '28px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '220px',
                  }}
                >
                  <div 
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                    }}
                  >
                    <Car className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                  </div>
                  <p style={{ fontSize: '17px', fontWeight: 700, color: '#1F2937', marginBottom: '4px', textAlign: 'center' }}>Selling Price</p>
                  <p style={{ fontSize: '13px', color: '#6B7280', textAlign: 'center' }}>(what you negotiate)</p>
                </div>

                {/* Residual Value Card - Charcoal gradient circle */}
                <div 
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
                    padding: '28px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '220px',
                  }}
                >
                  <div 
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1F2937, #4B5563)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                    }}
                  >
                    <Clock className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                  </div>
                  <p style={{ fontSize: '17px', fontWeight: 700, color: '#1F2937', marginBottom: '4px', textAlign: 'center' }}>Residual Value</p>
                  <p style={{ fontSize: '13px', color: '#6B7280', textAlign: 'center' }}>(car's worth at lease end)</p>
                </div>

                {/* Money Factor Card - Red gradient circle */}
                <div 
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
                    padding: '28px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '220px',
                  }}
                >
                  <div 
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #CC2200, #FF4136)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                    }}
                  >
                    <Percent className="w-6 h-6" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                  </div>
                  <p style={{ fontSize: '17px', fontWeight: 700, color: '#1F2937', marginBottom: '4px', textAlign: 'center' }}>Money Factor</p>
                  <p style={{ fontSize: '13px', color: '#6B7280', textAlign: 'center' }}>(interest as a decimal)</p>
                </div>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#1F2937', lineHeight: '1.85' }}>
                Add those together with taxes and fees, and that's your payment. Play with the numbers below to see exactly how each piece affects your total.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-screen-sm mx-auto px-5 py-20" style={{ backgroundColor: '#FFFFFF' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '64px' }}>

              {/* ======================= */}
              {/* WHAT YOU CAN CONTROL */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div className="px-8 py-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ 
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Target className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Negotiable vs. Fixed – What You Can Control</h2>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Negotiable Column */}
                    <div 
                      className="p-6"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                        minHeight: '200px',
                      }}
                    >
                      <div className="flex items-center gap-2 mb-6">
                        <span 
                          className="text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full"
                          style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)', color: '#001F3F' }}
                        >
                          You Negotiate
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                          <span className="text-sm font-medium" style={{ color: '#1F2937' }}>Selling Price</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                          <span className="text-sm font-medium" style={{ color: '#1F2937' }}>Doc Fee (sometimes)</span>
                        </div>
                      </div>
                    </div>
                    {/* Fixed Column */}
                    <div 
                      className="p-6"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                        minHeight: '200px',
                      }}
                    >
                      <div className="flex items-center gap-2 mb-6">
                        <span 
                          className="text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full"
                          style={{ backgroundColor: '#F0F0F0', color: '#1F2937' }}
                        >
                          Fixed / Set by Others
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm" style={{ color: '#1F2937' }}>Residual %</span>
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#F0F0F0', color: '#1F2937' }}>Bank</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm" style={{ color: '#1F2937' }}>Money Factor</span>
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#F0F0F0', color: '#1F2937' }}>Bank*</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm" style={{ color: '#1F2937' }}>Acquisition Fee</span>
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#F0F0F0', color: '#1F2937' }}>Bank</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm" style={{ color: '#1F2937' }}>Taxes</span>
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#F0F0F0', color: '#1F2937' }}>State</span>
                        </div>
                      </div>
                      <p className="text-xs mt-4" style={{ color: '#6B7280' }}>*Dealer may mark it up — always ask</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* VEHICLE PRICING */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div className="px-8 py-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ 
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Car className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Vehicle Pricing</h2>
                      <p className="text-sm mt-0.5" style={{ color: '#1F2937' }}>Start with sticker and negotiated prices</p>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  {/* MSRP */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>MSRP (Sticker Price)</label>
                        <button
                          onClick={() => setActiveTooltip('msrp')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="msrp-info-btn"
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.msrp}
                        onChange={(val) => setLeaseInputs({...leaseInputs, msrp: val})}
                        min={5000}
                        max={150000}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={5000}
                      max={150000}
                      step={500}
                      value={leaseInputs.msrp}
                      onChange={(e) => setLeaseInputs({...leaseInputs, msrp: parseFloat(e.target.value) || 0})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseInputs.msrp - 5000) / (150000 - 5000)) * 100}%, #E5E7EB ${((leaseInputs.msrp - 5000) / (150000 - 5000)) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-msrp-input"
                    />
                  </div>

                  {/* Selling Price */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Selling Price (Negotiated Price)</label>
                        <button
                          onClick={() => setActiveTooltip('sellingPrice')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="selling-price-info-btn"
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.sellingPrice}
                        onChange={(val) => setLeaseInputs({...leaseInputs, sellingPrice: val})}
                        min={5000}
                        max={150000}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={5000}
                      max={150000}
                      step={500}
                      value={leaseInputs.sellingPrice}
                      onChange={(e) => setLeaseInputs({...leaseInputs, sellingPrice: parseFloat(e.target.value) || 0})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseInputs.sellingPrice - 5000) / (150000 - 5000)) * 100}%, #E5E7EB ${((leaseInputs.sellingPrice - 5000) / (150000 - 5000)) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-selling-price-input"
                    />
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* CAP COST REDUCTION */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div className="px-8 py-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ 
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1F2937, #4B5563)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DollarSign className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Money You Apply Toward the Lease (Optional)</h2>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  {/* Incentives/Rebates */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Incentives / Rebates</label>
                        <button
                          onClick={() => setActiveTooltip('incentives')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="incentives-info-btn"
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.incentives}
                        onChange={(val) => setLeaseInputs({...leaseInputs, incentives: val})}
                        min={0}
                        max={10000}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={10000}
                      step={250}
                      value={leaseInputs.incentives}
                      onChange={(e) => setLeaseInputs({...leaseInputs, incentives: parseFloat(e.target.value) || 0})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(leaseInputs.incentives / 10000) * 100}%, #E5E7EB ${(leaseInputs.incentives / 10000) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-incentives-input"
                    />
                  </div>

                  {/* Cash Down Payment */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Cash Down Payment (Cap Reduction)</label>
                        <button
                          onClick={() => setActiveTooltip('cashDown')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(255, 65, 54, 0.1)' }}
                          data-testid="cash-down-info-btn"
                        >
                          <AlertTriangle className="w-3 h-3" style={{ color: '#FF4136' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.cashDown}
                        onChange={(val) => setLeaseInputs({...leaseInputs, cashDown: val})}
                        min={0}
                        max={20000}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={20000}
                      step={250}
                      value={leaseInputs.cashDown}
                      onChange={(e) => setLeaseInputs({...leaseInputs, cashDown: parseFloat(e.target.value) || 0})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(leaseInputs.cashDown / 20000) * 100}%, #E5E7EB ${(leaseInputs.cashDown / 20000) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-cash-down-input"
                    />
                    {/* Point 12: Improved Down Payment Warning */}
                    {leaseInputs.cashDown > 0 && (
                      <div 
                        className="mt-3 p-4 flex items-start gap-3"
                        style={{ 
                          backgroundColor: '#FFFFFF', 
                          borderRadius: '12px',
                          border: '1px solid #FF4136',
                        }}
                      >
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#FF4136' }} strokeWidth={2} />
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#FF4136' }}>
                            Lower payment but increases financial risk.
                          </p>
                          <p className="text-xs mt-1" style={{ color: '#FF4136' }}>
                            If the car is totaled early, the down payment is usually not recovered.
                          </p>
                        </div>
                      </div>
                    )}
                    {/* 0 Down vs Cash Down comparison */}
                    {leaseInputs.cashDown > 0 && (
                      <div 
                        className="mt-3 p-4 rounded-xl"
                        style={{ backgroundColor: '#F8F9FA', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                      >
                        <p className="text-xs font-semibold mb-2" style={{ color: '#001F3F' }}>$0 Down vs. ${leaseInputs.cashDown.toLocaleString()} Down</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs" style={{ color: '#6B7280' }}>$0 down payment:</p>
                            <p className="text-sm font-semibold" style={{ color: '#001F3F' }}>
                              ${(leaseResults.totalMonthlyPayment + (leaseInputs.cashDown / leaseInputs.termMonths) * (1 + leaseInputs.taxRate / 100)).toFixed(2)}/mo
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: '#6B7280' }}>${leaseInputs.cashDown.toLocaleString()} down:</p>
                            <p className="text-sm font-semibold" style={{ color: '#001F3F' }}>
                              ${leaseResults.totalMonthlyPayment.toFixed(2)}/mo
                            </p>
                          </div>
                        </div>
                        <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                          Same total cost — but $0 down keeps your cash safe if anything happens.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Trade-in equity */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Trade-In Equity</label>
                        <button
                          onClick={() => setActiveTooltip('tradeEquity')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="trade-equity-info-btn"
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.tradeEquity}
                        onChange={(val) => setLeaseInputs({...leaseInputs, tradeEquity: val})}
                        min={0}
                        max={30000}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={30000}
                      step={500}
                      value={leaseInputs.tradeEquity}
                      onChange={(e) => setLeaseInputs({...leaseInputs, tradeEquity: parseFloat(e.target.value) || 0})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(leaseInputs.tradeEquity / 30000) * 100}%, #E5E7EB ${(leaseInputs.tradeEquity / 30000) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-trade-equity-input"
                    />
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* LEASE TERMS */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div className="px-8 py-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #1F2937, #4B5563)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Clock className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Lease Terms</h2>
                      <p className="text-sm mt-0.5" style={{ color: '#1F2937' }}>Term length, residual, and money factor</p>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  {/* Term */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Lease Term (Months)</label>
                      <button
                        onClick={() => setActiveTooltip('leaseTerm')}
                        className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                        style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                        data-testid="lease-term-info-btn"
                      >
                        <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {[24, 36, 48, 60].map((term) => (
                        <button
                          key={term}
                          onClick={() => setLeaseInputs({...leaseInputs, termMonths: term})}
                          className="py-3.5 px-4 rounded-xl text-base font-semibold transition-all"
                          style={{ 
                            backgroundColor: leaseInputs.termMonths === term ? '#001F3F' : '#FFFFFF',
                            color: leaseInputs.termMonths === term ? '#FFFFFF' : '#6B7280',
                            border: leaseInputs.termMonths === term ? '2px solid #001F3F' : '2px solid #E5E7EB',
                          }}
                          data-testid={`lease-term-${term}`}
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Residual % */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Bank residual %</label>
                        <button
                          onClick={() => setActiveTooltip('residual')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="residual-info-btn"
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.residualPercent}
                        onChange={(val) => setLeaseInputs({...leaseInputs, residualPercent: val})}
                        min={30}
                        max={75}
                        suffix="%"
                      />
                    </div>
                    <input
                      type="range"
                      min={30}
                      max={75}
                      step={1}
                      value={leaseInputs.residualPercent}
                      onChange={(e) => setLeaseInputs({...leaseInputs, residualPercent: parseInt(e.target.value)})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseInputs.residualPercent - 30) / 45) * 100}%, #E5E7EB ${((leaseInputs.residualPercent - 30) / 45) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-residual-slider"
                    />
                  </div>

                  {/* Money Factor */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Money Factor</label>
                        <button
                          onClick={() => setActiveTooltip('moneyFactor')}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="money-factor-info-btn"
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseInputs.moneyFactor}
                        onChange={(val) => setLeaseInputs({...leaseInputs, moneyFactor: val})}
                        min={0.0005}
                        max={0.005}
                        decimals={5}
                        formatCommas={false}
                      />
                    </div>
                    <input
                      type="range"
                      min={5}
                      max={50}
                      step={1}
                      value={leaseInputs.moneyFactor * 10000}
                      onChange={(e) => setLeaseInputs({...leaseInputs, moneyFactor: parseInt(e.target.value) / 10000})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseInputs.moneyFactor * 10000 - 5) / 45) * 100}%, #E5E7EB ${((leaseInputs.moneyFactor * 10000 - 5) / 45) * 100}%, #E5E7EB 100%)`,
                      }}
                      data-testid="lease-money-factor-slider"
                    />
                    {/* Warning when money factor > 0.00180 */}
                    {leaseInputs.moneyFactor > 0.00180 && (
                      <p className="text-xs font-medium" style={{ color: '#EF4444' }}>
                        Above recommended max — dealer may be marking this up
                      </p>
                    )}
                  </div>

                  {/* Annual Mileage Allowance */}
                  <div>
                    <label className="text-sm font-semibold mb-3 block" style={{ color: '#1F2937' }}>Annual Mileage Allowance</label>
                    <div className="flex gap-3">
                      {[10000, 12000, 15000].map((miles) => (
                        <button
                          key={miles}
                          onClick={() => setLeaseInputs({...leaseInputs, annualMileage: miles})}
                          className="flex-1 py-3 px-4 rounded-full text-sm font-semibold transition-all"
                          style={{ 
                            backgroundColor: leaseInputs.annualMileage === miles ? '#001F3F' : '#FFFFFF',
                            color: leaseInputs.annualMileage === miles ? '#FFFFFF' : '#6B7280',
                            border: leaseInputs.annualMileage === miles ? '2px solid #001F3F' : '2px solid #E5E7EB',
                          }}
                          data-testid={`lease-mileage-${miles}`}
                        >
                          {miles.toLocaleString()}
                        </button>
                      ))}
                    </div>
                    {/* Point 11: Mileage Cost Warning */}
                    <div 
                      className="mt-4 p-4 flex items-start gap-3"
                      style={{ backgroundColor: '#F8F9FA', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)' }}
                    >
                      <Gauge className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#001F3F' }} strokeWidth={2} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#1F2937' }}>
                          {leaseInputs.annualMileage.toLocaleString()} miles/year
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                          Driving 3,000 extra miles could cost $450–$750 at lease end.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* FEES & TAXES */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div className="px-8 py-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #CC2200, #FF4136)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Percent className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Fees & Taxes</h2>
                      <p className="text-sm mt-0.5" style={{ color: '#1F2937' }}>Bank fees, doc fees, and tax settings</p>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  {/* Fees Row */}
                  <div className="grid grid-cols-3 gap-5">
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <label className="text-xs font-semibold" style={{ color: '#1F2937' }}>Acquisition Fee</label>
                        <button
                          onClick={() => setActiveTooltip('acquisitionFee')}
                          className="w-4 h-4 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="acquisition-fee-info-btn"
                        >
                          <Info className="w-2.5 h-2.5" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium" style={{ color: '#6B7280' }}>$</span>
                        <input
                          type="number"
                          value={leaseInputs.acquisitionFee}
                          onChange={(e) => setLeaseInputs({...leaseInputs, acquisitionFee: parseFloat(e.target.value) || 0})}
                          className="w-full pl-6 pr-2 py-3 text-sm font-semibold rounded-xl transition-all focus:ring-2 focus:ring-blue-500"
                          style={{ border: '2px solid #E5E7EB', backgroundColor: '#FFFFFF', color: '#001F3F' }}
                          data-testid="lease-acquisition-fee-input"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <label className="text-xs font-semibold" style={{ color: '#1F2937' }}>Doc Fee</label>
                        <button
                          onClick={() => setActiveTooltip('docFee')}
                          className="w-4 h-4 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="doc-fee-info-btn"
                        >
                          <Info className="w-2.5 h-2.5" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium" style={{ color: '#6B7280' }}>$</span>
                        <input
                          type="number"
                          value={leaseInputs.docFee}
                          onChange={(e) => setLeaseInputs({...leaseInputs, docFee: parseFloat(e.target.value) || 0})}
                          className="w-full pl-6 pr-2 py-3 text-sm font-semibold rounded-xl transition-all focus:ring-2 focus:ring-blue-500"
                          style={{ border: '2px solid #E5E7EB', backgroundColor: '#FFFFFF', color: '#001F3F' }}
                          data-testid="lease-doc-fee-input"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <label className="text-xs font-semibold" style={{ color: '#1F2937' }}>Other Fees</label>
                        <button
                          onClick={() => setActiveTooltip('otherFees')}
                          className="w-4 h-4 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="other-fees-info-btn"
                        >
                          <Info className="w-2.5 h-2.5" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium" style={{ color: '#6B7280' }}>$</span>
                        <input
                          type="number"
                          value={leaseInputs.otherFees}
                          onChange={(e) => setLeaseInputs({...leaseInputs, otherFees: parseFloat(e.target.value) || 0})}
                          className="w-full pl-6 pr-2 py-3 text-sm font-semibold rounded-xl transition-all focus:ring-2 focus:ring-blue-500"
                          style={{ border: '2px solid #E5E7EB', backgroundColor: '#FFFFFF', color: '#001F3F' }}
                          data-testid="lease-other-fees-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tax Rate & Mode */}
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <label className="text-sm font-semibold" style={{ color: '#1F2937' }}>Tax Rate %</label>
                        <button
                          onClick={() => setActiveTooltip('taxRate')}
                          className="w-4 h-4 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(30, 58, 138, 0.1)' }}
                          data-testid="tax-rate-info-btn"
                        >
                          <Info className="w-2.5 h-2.5" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </div>
                      <input
                        type="number"
                        step="0.1"
                        value={leaseInputs.taxRate}
                        onChange={(e) => setLeaseInputs({...leaseInputs, taxRate: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-3.5 text-base font-semibold rounded-xl transition-all focus:ring-2 focus:ring-blue-500"
                        style={{ border: '2px solid #E5E7EB', backgroundColor: '#FFFFFF', color: '#001F3F' }}
                        data-testid="lease-tax-rate-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#1F2937' }}>Tax Mode</label>
                      <select
                        value={leaseInputs.taxMode}
                        onChange={(e) => setLeaseInputs({...leaseInputs, taxMode: e.target.value})}
                        className="w-full px-4 py-3.5 text-base font-semibold rounded-xl transition-all focus:ring-2 focus:ring-blue-500"
                        style={{ border: '2px solid #E5E7EB', backgroundColor: '#FFFFFF', color: '#001F3F' }}
                        data-testid="lease-tax-mode-select"
                      >
                        <option value="monthly">Monthly (most states)</option>
                        <option value="upfront">Upfront</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* COMMON LEASE PRICING FACTORS (Point 9) */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div className="px-8 py-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #CC2200, #FF4136)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <AlertCircle className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Common Lease Pricing Factors</h2>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{ backgroundColor: '#F8F9FA', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.04)' }}
                    >
                      <div 
                        style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #CC2200, #FF4136)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <Percent className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span className="text-sm" style={{ color: '#1F2937' }}>Marked-up money factor</span>
                    </div>
                    <div 
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{ backgroundColor: '#F8F9FA', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.04)' }}
                    >
                      <div 
                        style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #CC2200, #FF4136)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <DollarSign className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span className="text-sm" style={{ color: '#1F2937' }}>Higher selling price</span>
                    </div>
                    <div 
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{ backgroundColor: '#F8F9FA', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.04)' }}
                    >
                      <div 
                        style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #CC2200, #FF4136)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <FileText className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span className="text-sm" style={{ color: '#1F2937' }}>High doc fees</span>
                    </div>
                    <div 
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{ backgroundColor: '#F8F9FA', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.04)' }}
                    >
                      <div 
                        style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #CC2200, #FF4136)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <AlertTriangle className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                      </div>
                      <span className="text-sm" style={{ color: '#1F2937' }}>Negative trade equity rollover</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* WHAT TO ASK THE DEALER */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div 
                  className="px-8 py-6" 
                  style={{ borderBottom: '1px solid #E5E7EB' }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <CheckCircle className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>What to Ask the Dealer (Before You Sign)</h2>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-8">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div 
                      className="p-5 rounded-xl flex items-center gap-4 transition-shadow hover:shadow-md"
                      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', minHeight: '60px' }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                      </div>
                      <p className="text-sm font-medium flex items-center" style={{ color: '#1F2937' }}>
                        What is the exact residual %?
                        <button
                          onClick={() => setActiveTooltip('dealerResidual')}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full ml-2 hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)' }}
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </p>
                    </div>
                    <div 
                      className="p-5 rounded-xl flex items-center gap-4 transition-shadow hover:shadow-md"
                      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', minHeight: '60px' }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                      </div>
                      <p className="text-sm font-medium flex items-center" style={{ color: '#1F2937' }}>
                        Is this the bank's buy-rate money factor or marked up?
                        <button
                          onClick={() => setActiveTooltip('dealerBuyRate')}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full ml-2 hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)' }}
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </p>
                    </div>
                    <div 
                      className="p-5 rounded-xl flex items-center gap-4 transition-shadow hover:shadow-md"
                      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', minHeight: '60px' }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                      </div>
                      <p className="text-sm font-medium flex items-center" style={{ color: '#1F2937' }}>
                        What is the adjusted cap cost?
                        <button
                          onClick={() => setActiveTooltip('dealerCapCost')}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full ml-2 hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)' }}
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </p>
                    </div>
                    <div 
                      className="p-5 rounded-xl flex items-center gap-4 transition-shadow hover:shadow-md"
                      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', minHeight: '60px' }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                      </div>
                      <p className="text-sm font-medium flex items-center" style={{ color: '#1F2937' }}>
                        Are any add-ons rolled into the lease?
                        <button
                          onClick={() => setActiveTooltip('dealerAddOns')}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full ml-2 hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)' }}
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </p>
                    </div>
                    <div 
                      className="p-5 rounded-xl flex items-center gap-4 transition-shadow hover:shadow-md"
                      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', minHeight: '60px' }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Check className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                      </div>
                      <p className="text-sm font-medium flex items-center" style={{ color: '#1F2937' }}>
                        What is the disposition fee at the end? (usually $300–$500)
                        <button
                          onClick={() => setActiveTooltip('dealerDisposition')}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full ml-2 hover:opacity-70 transition-opacity"
                          style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)' }}
                        >
                          <Info className="w-3 h-3" style={{ color: '#001F3F' }} strokeWidth={2.5} />
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================= */}
              {/* PAYMENT BREAKDOWN */}
              {/* ======================= */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  overflow: 'hidden',
                }}
              >
                <div 
                  className="px-8 py-6" 
                  style={{ borderBottom: '1px solid #E5E7EB' }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <BarChart3 className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#1F2937' }}>Your Lease Payment Breakdown</h2>
                      <span 
                        className="text-xs px-2.5 py-1 rounded-full mt-1.5 inline-block font-medium"
                        style={{ backgroundColor: 'rgba(0, 31, 63, 0.1)', color: '#001F3F' }}
                      >
                        Live calculation
                      </span>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-10" style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                  
                  {/* Point 3: Effective Vehicle Price Used in Lease */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <p className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: '#001F3F' }}>Vehicle price used for lease math</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>Selling price</span>
                        <span style={{ color: '#1F2937' }}>${leaseInputs.sellingPrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>− Incentives</span>
                        <span style={{ color: '#1F2937' }}>−${leaseInputs.incentives.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>− Cash down</span>
                        <span style={{ color: '#1F2937' }}>−${leaseInputs.cashDown.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>{leaseInputs.tradeEquity >= 0 ? '−' : '+'} Trade equity</span>
                        <span style={{ color: '#1F2937' }}>{leaseInputs.tradeEquity >= 0 ? '−' : '+'}${Math.abs(leaseInputs.tradeEquity).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>+ Fees</span>
                        <span style={{ color: '#1F2937' }}>+${(leaseInputs.acquisitionFee + leaseInputs.docFee + leaseInputs.otherFees).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold pt-2" style={{ borderTop: '1px solid #E5E7EB' }}>
                        <span style={{ color: '#001F3F' }}>= Adjusted Cap Cost</span>
                        <span style={{ color: '#001F3F' }}>${leaseResults.adjustedCapCost.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Point 19: Visual Graphic - Depreciation You Pay */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                      {/* Car Price Card - Navy to Steel Blue gradient */}
                      <div 
                        className="text-center p-4 rounded-xl w-full"
                        style={{ background: 'linear-gradient(180deg, #001F3F 0%, #2A4A6B 100%)' }}
                      >
                        <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Car price</p>
                        <p className="text-lg font-bold" style={{ color: '#FFFFFF' }}>${leaseInputs.sellingPrice.toLocaleString()}</p>
                      </div>
                      <ChevronDown className="w-5 h-5" style={{ color: '#D1D5DB' }} />
                      {/* Residual Value Card - Solid Charcoal */}
                      <div 
                        className="text-center p-4 rounded-xl w-full"
                        style={{ backgroundColor: '#1F2937' }}
                      >
                        <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Residual value</p>
                        <p className="text-lg font-bold" style={{ color: '#FFFFFF' }}>${(leaseInputs.msrp * leaseInputs.residualPercent / 100).toLocaleString()}</p>
                      </div>
                      <ChevronDown className="w-5 h-5" style={{ color: '#D1D5DB' }} />
                      {/* Depreciation You Pay - Dark Red to Light Red gradient */}
                      <div 
                        className="text-center p-4 rounded-xl w-full"
                        style={{ background: 'linear-gradient(180deg, #CC3333 0%, #FF4136 100%)' }}
                      >
                        <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Depreciation you pay</p>
                        <p className="text-xl font-bold" style={{ color: '#FFFFFF' }}>${(leaseResults.depreciation * leaseInputs.termMonths).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                      </div>
                    </div>
                  </div>

                  {/* Point 4: Highlight Depreciation and Finance Charge with colors - White bg with colored left borders */}
                  <div className="grid grid-cols-2 gap-6">
                    <div 
                      className="text-center p-6"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        borderRadius: '0 24px 24px 0', 
                        boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)', 
                        minHeight: '150px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        borderLeft: '4px solid #001F3F',
                      }}
                    >
                      <p className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: '#1F2937' }}>Depreciation Cost</p>
                      <p className="text-3xl font-bold" style={{ color: '#001F3F' }}>
                        ${leaseResults.depreciation.toFixed(2)}
                      </p>
                      <p className="text-sm mt-2" style={{ color: '#6B7280' }}>per month</p>
                    </div>
                    <div 
                      className="text-center p-6"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        borderRadius: '0 24px 24px 0', 
                        boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)', 
                        minHeight: '150px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        borderLeft: '4px solid #FF4136',
                      }}
                    >
                      <p className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: '#1F2937' }}>Finance Cost</p>
                      <p className="text-3xl font-bold" style={{ color: '#FF4136' }}>
                        ${leaseResults.financeCharge.toFixed(2)}
                      </p>
                      <p className="text-sm mt-2" style={{ color: '#6B7280' }}>per month</p>
                    </div>
                  </div>

                  {/* Point 17: Formula Labels - Base + Taxes + Final */}
                  <div className="grid grid-cols-3 gap-4">
                    <div 
                      className="text-center p-5"
                      style={{ backgroundColor: '#FFFFFF', borderRadius: '20px', border: '1px solid #E5E7EB', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.04)' }}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Base Payment</p>
                      <p className="text-xl font-bold" style={{ color: '#1F2937' }}>
                        ${leaseResults.basePayment.toFixed(2)}
                      </p>
                    </div>
                    <div 
                      className="text-center p-5"
                      style={{ backgroundColor: '#FFFFFF', borderRadius: '20px', border: '1px solid #E5E7EB', boxShadow: '0 2px 8px rgba(0, 31, 63, 0.04)' }}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Taxes</p>
                      <p className="text-xl font-bold" style={{ color: '#1F2937' }}>
                        ${(leaseResults.totalMonthlyPayment - leaseResults.basePayment).toFixed(2)}
                      </p>
                    </div>
                    <div 
                      className="text-center p-5"
                      style={{ backgroundColor: '#001F3F', borderRadius: '20px' }}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>Final Monthly</p>
                      <p className="text-xl font-bold" style={{ color: '#FFFFFF' }}>
                        ${leaseResults.totalMonthlyPayment.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Total Monthly Payment - Big Display with Navy Gradient */}
                  <div 
                    className="text-center p-10"
                    style={{ 
                      background: 'linear-gradient(180deg, #001F3F 0%, #0A4A7A 100%)',
                      borderRadius: '24px',
                    }}
                  >
                    <p className="text-sm font-bold uppercase tracking-wide mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>TOTAL MONTHLY</p>
                    <p className="text-6xl font-black" style={{ color: '#FFFFFF', letterSpacing: '-3px' }}>
                      ${leaseResults.totalMonthlyPayment.toFixed(2)}
                    </p>
                    
                    {/* Point 1: Live Payment Impact Bar - lighter navy banner */}
                    <div 
                      className="mt-6 p-4 rounded-xl"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                        Every $1,000 change in price ≈ <strong style={{ color: '#FFFFFF' }}>${Math.round(1000 / leaseInputs.termMonths)}/month</strong>
                      </p>
                    </div>

                    {/* Point 2: Cost Per Day */}
                    <div className="mt-4">
                      <p className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>${leaseResults.totalMonthlyPayment.toFixed(2)} per month</p>
                      <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>≈ ${(leaseResults.totalMonthlyPayment / 30).toFixed(2)} per day</p>
                    </div>

                    {/* Point 18: Lease vs MSRP Percentage */}
                    <div className="mt-4">
                      <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Payment = <strong style={{ color: '#FFFFFF' }}>{((leaseResults.totalMonthlyPayment / leaseInputs.msrp) * 100).toFixed(2)}%</strong> of MSRP
                      </p>
                    </div>
                  </div>

                  {/* Point 13: Real Monthly Budget Impact */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <p className="text-sm font-semibold mb-4" style={{ color: '#1F2937' }}>This payment equals:</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold" style={{ color: '#001F3F' }}>${leaseResults.totalMonthlyPayment.toFixed(0)}</p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>/month</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold" style={{ color: '#001F3F' }}>${Math.round(leaseResults.totalMonthlyPayment / 4.33)}</p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>/week</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold" style={{ color: '#001F3F' }}>${(leaseResults.totalMonthlyPayment / 30).toFixed(0)}</p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>/day</p>
                      </div>
                    </div>
                  </div>

                  {/* Premium UI Tweak: How dealers structure this payment - White bg with colored borders */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <p className="text-sm font-semibold mb-4" style={{ color: '#1F2937' }}>How dealers structure this payment</p>
                    <p className="text-xs mb-4" style={{ color: '#6B7280' }}>Your ${leaseResults.totalMonthlyPayment.toFixed(0)} payment is made of:</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '2px solid #001F3F' }}>
                        <span className="text-sm" style={{ color: '#001F3F' }}>Depreciation</span>
                        <span className="text-sm font-bold" style={{ color: '#1F2937' }}>${leaseResults.depreciation.toFixed(0)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '2px solid #FF4136' }}>
                        <span className="text-sm" style={{ color: '#FF4136' }}>Finance cost</span>
                        <span className="text-sm font-bold" style={{ color: '#FF4136' }}>${leaseResults.financeCharge.toFixed(0)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '2px solid #1F2937' }}>
                        <span className="text-sm" style={{ color: '#1F2937' }}>Taxes</span>
                        <span className="text-sm font-bold" style={{ color: '#1F2937' }}>${(leaseResults.totalMonthlyPayment - leaseResults.basePayment).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Stats - Total Paid */}
                  <div className="grid grid-cols-2 gap-6">
                    <div 
                      className="text-center p-6"
                      style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)', minHeight: '110px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      <p className="text-sm font-medium mb-2" style={{ color: '#6B7280' }}>Total Paid Over Term</p>
                      <p className="text-2xl font-bold" style={{ color: '#1F2937' }}>
                        ${leaseResults.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div 
                      className="text-center p-6"
                      style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)', minHeight: '110px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      <p className="text-sm font-medium mb-2" style={{ color: '#6B7280' }}>Total Interest Paid</p>
                      <p className="text-2xl font-bold" style={{ color: '#DC2626' }}>
                        ${leaseResults.totalInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                  </div>

                  {/* Point 5: Total Cost vs Buying Benchmark */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <p className="text-sm font-semibold mb-4" style={{ color: '#1F2937' }}>Estimated vehicle value used during lease:</p>
                    <p className="text-2xl font-bold mb-4" style={{ color: '#001F3F' }}>${leaseResults.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })} paid</p>
                    <p className="text-sm mb-2" style={{ color: '#6B7280' }}>Typical purchase cost of similar vehicle:</p>
                    <p className="text-lg font-semibold" style={{ color: '#1F2937' }}>${(leaseInputs.msrp * 0.95).toLocaleString('en-US', { maximumFractionDigits: 0 })}–${(leaseInputs.msrp * 1.0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                  </div>

                  {/* Point 10: Lease vs Buy Snapshot */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#FFFFFF', borderRadius: '24px', boxShadow: '0 4px 24px rgba(0, 31, 63, 0.08)' }}
                  >
                    <p className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: '#001F3F' }}>Lease Snapshot</p>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs uppercase tracking-wide mb-1" style={{ color: '#6B7280' }}>Monthly payment</p>
                        <p className="text-xl font-bold" style={{ color: '#001F3F' }}>${leaseResults.totalMonthlyPayment.toFixed(0)}</p>
                        <p className="text-xs uppercase tracking-wide mt-3 mb-1" style={{ color: '#6B7280' }}>Total paid</p>
                        <p className="text-xl font-bold" style={{ color: '#001F3F' }}>${leaseResults.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div style={{ borderLeft: '1px solid #E5E7EB', paddingLeft: '24px' }}>
                        <p className="text-xs uppercase tracking-wide mb-1" style={{ color: '#6B7280' }}>Purchase estimate</p>
                        <p className="text-lg font-semibold" style={{ color: '#6B7280' }}>≈ ${Math.round(leaseInputs.msrp * 0.018)}–${Math.round(leaseInputs.msrp * 0.02)}/month</p>
                        <p className="text-xs uppercase tracking-wide mt-3 mb-1" style={{ color: '#6B7280' }}>Total cost</p>
                        <p className="text-lg font-semibold" style={{ color: '#6B7280' }}>≈ ${(leaseInputs.msrp * 1.05).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                      </div>
                    </div>
                  </div>

                  {/* Point 6: Lease Deal Score with rating scale */}
                  {leaseScore && (
                    <div 
                      className="p-8"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        borderRadius: '24px',
                        borderTop: '3px solid #001F3F',
                        boxShadow: '0 2px 8px rgba(0, 31, 63, 0.08)',
                      }}
                      data-testid="lease-deal-score"
                    >
                      <p className="text-sm font-semibold uppercase tracking-wide mb-4 text-center" style={{ color: '#001F3F' }}>Lease Deal Score</p>
                      <p className="text-3xl font-bold mb-3 text-center" style={{ color: '#001F3F' }}>
                        {leaseScore.ratio.toFixed(2)}% of MSRP
                      </p>
                      <p className="text-xl font-semibold mb-6 text-center" style={{ color: leaseScore.color }}>
                        {leaseScore.label}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="flex justify-between items-center text-sm">
                          <span style={{ color: '#001F3F', fontWeight: leaseScore.tier === 'Excellent' ? 700 : 400 }}>
                            {leaseScore.tier === 'Excellent' && '▶ '}Excellent
                          </span>
                          <span style={{ color: '#6B7280' }}>≤ 1.0%</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span style={{ color: '#001F3F', fontWeight: leaseScore.tier === 'Good' ? 700 : 400 }}>
                            {leaseScore.tier === 'Good' && '▶ '}Good
                          </span>
                          <span style={{ color: '#6B7280' }}>≤ 1.25%</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span style={{ color: '#1F2937', fontWeight: leaseScore.tier === 'Fair' ? 700 : 400 }}>
                            {leaseScore.tier === 'Fair' && '▶ '}Fair
                          </span>
                          <span style={{ color: '#6B7280' }}>≤ 1.5%</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span style={{ color: '#FF4136', fontWeight: leaseScore.tier === 'High' ? 700 : 400 }}>
                            {leaseScore.tier === 'High' && '▶ '}High
                          </span>
                          <span style={{ color: '#6B7280' }}>≥ 1.5%</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Point 16: Smart Tip Generator - Navy left border */}
                  <div 
                    className="p-6"
                    style={{ 
                      backgroundColor: '#FFFFFF', 
                      borderRadius: '0 24px 24px 0',
                      borderLeft: '4px solid #001F3F',
                      boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)',
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <Lightbulb className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1" style={{ color: '#001F3F' }}>Tip</p>
                        <p className="text-sm" style={{ color: '#1F2937' }}>
                          Reducing the price by $1,500 would lower your payment by about ${Math.round(1500 / leaseInputs.termMonths)}/month.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Point 14: Try These Scenarios Buttons */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm font-semibold" style={{ color: '#1F2937' }}>Try:</p>
                      <button
                        onClick={() => setLeaseInputs({
                          msrp: 40000,
                          sellingPrice: 38000,
                          residualPercent: 57,
                          moneyFactor: 0.00125,
                          termMonths: 36,
                          taxRate: 8.25,
                          acquisitionFee: 795,
                          docFee: 150,
                          otherFees: 0,
                          incentives: 0,
                          cashDown: 2000,
                          tradeEquity: 0,
                          annualMileage: 12000,
                        })}
                        className="text-xs font-medium hover:opacity-70 transition-opacity"
                        style={{ color: '#FF4136' }}
                      >
                        Reset
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setLeaseInputs({...leaseInputs, cashDown: 0})}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all hover:bg-navy hover:text-white text-center"
                        style={{ backgroundColor: '#FFFFFF', color: '#001F3F', border: '1px solid #001F3F' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#001F3F'; e.currentTarget.style.color = '#FFFFFF'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.color = '#001F3F'; }}
                        data-testid="try-zero-down"
                      >
                        $0 down
                      </button>
                      <button
                        onClick={() => setLeaseInputs({...leaseInputs, moneyFactor: (leaseInputs.moneyFactor + 0.00083)})}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all text-center"
                        style={{ backgroundColor: '#FFFFFF', color: '#001F3F', border: '1px solid #001F3F' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#001F3F'; e.currentTarget.style.color = '#FFFFFF'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.color = '#001F3F'; }}
                        data-testid="try-higher-apr"
                      >
                        +2% interest
                      </button>
                      <button
                        onClick={() => setLeaseInputs({...leaseInputs, sellingPrice: leaseInputs.sellingPrice - 1000})}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all text-center"
                        style={{ backgroundColor: '#FFFFFF', color: '#001F3F', border: '1px solid #001F3F' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#001F3F'; e.currentTarget.style.color = '#FFFFFF'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.color = '#001F3F'; }}
                        data-testid="try-lower-price"
                      >
                        $1,000 lower price
                      </button>
                      <button
                        onClick={() => setLeaseInputs({...leaseInputs, termMonths: 48})}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all text-center"
                        style={{ backgroundColor: '#FFFFFF', color: '#001F3F', border: '1px solid #001F3F' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#001F3F'; e.currentTarget.style.color = '#FFFFFF'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.color = '#001F3F'; }}
                        data-testid="try-48-month"
                      >
                        48 month lease
                      </button>
                    </div>
                  </div>

                  {/* Point 15: Shareable Result Card */}
                  <div 
                    className="p-6"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                    data-testid="shareable-result-card"
                  >
                    <p className="text-xs uppercase tracking-wide mb-4 font-semibold" style={{ color: '#001F3F' }}>VINdicated Lease Check</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs" style={{ color: '#6B7280' }}>Vehicle</p>
                        <p className="text-lg font-bold" style={{ color: '#1F2937' }}>${leaseInputs.msrp.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#6B7280' }}>Monthly</p>
                        <p className="text-lg font-bold" style={{ color: '#001F3F' }}>${leaseResults.totalMonthlyPayment.toFixed(0)}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#6B7280' }}>Deal score</p>
                        <p className="text-lg font-bold" style={{ color: leaseScore ? leaseScore.color : '#1F2937' }}>{leaseScore ? leaseScore.label.split(' ')[0] : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#6B7280' }}>APR</p>
                        <p className="text-lg font-bold" style={{ color: '#1F2937' }}>{leaseResults.aprEquivalent.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>

                  {/* Money Factor to APR note */}
                  <div 
                    className="p-5 rounded-xl"
                    style={{ backgroundColor: '#F8F9FA', boxShadow: '0 4px 16px rgba(0, 31, 63, 0.06)' }}
                  >
                    <p className="text-sm font-medium" style={{ color: '#001F3F' }}>
                      <strong>Money factor to APR:</strong> {leaseInputs.moneyFactor.toFixed(5)} × 2400 = {leaseResults.aprEquivalent.toFixed(2)}% APR
                    </p>
                  </div>

                  {/* Point 17: See the Formula with improved labels */}
                  <div
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                      overflow: 'hidden',
                    }}
                  >
                    <div className="mb-4">
                      <span className="text-sm font-semibold" style={{ color: '#001F3F' }}>See the Formula</span>
                    </div>
                    <div>
                      <div 
                        className="p-5 rounded-xl font-mono text-sm"
                        style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB' }}
                      >
                        <p className="mb-3" style={{ color: '#1F2937' }}>
                          <strong style={{ color: '#001F3F' }}>Depreciation Cost</strong> = (Adjusted Cap Cost – Residual) ÷ Term
                        </p>
                        <p className="mb-5 text-xs" style={{ color: '#6B7280' }}>
                          = (${leaseResults.adjustedCapCost.toLocaleString()} – ${(leaseInputs.msrp * leaseInputs.residualPercent / 100).toLocaleString()}) ÷ {leaseInputs.termMonths}
                          <br />= <strong>${leaseResults.depreciation.toFixed(2)}/mo</strong>
                        </p>
                        <p className="mb-3" style={{ color: '#1F2937' }}>
                          <strong style={{ color: '#FF4136' }}>Finance Cost</strong> = (Adjusted Cap Cost + Residual) × Money Factor
                        </p>
                        <p className="mb-5 text-xs" style={{ color: '#6B7280' }}>
                          = (${leaseResults.adjustedCapCost.toLocaleString()} + ${(leaseInputs.msrp * leaseInputs.residualPercent / 100).toLocaleString()}) × {leaseInputs.moneyFactor.toFixed(5)}
                          <br />= <strong>${leaseResults.financeCharge.toFixed(2)}/mo</strong>
                        </p>
                        <p className="mb-3" style={{ color: '#1F2937' }}>
                          <strong style={{ color: '#001F3F' }}>Base Payment</strong> = Depreciation + Finance
                        </p>
                        <p className="mb-5 text-xs" style={{ color: '#6B7280' }}>
                          = ${leaseResults.depreciation.toFixed(2)} + ${leaseResults.financeCharge.toFixed(2)}
                          <br />= <strong>${leaseResults.basePayment.toFixed(2)}/mo</strong>
                        </p>
                        <p className="mb-3" style={{ color: '#1F2937' }}>
                          <strong style={{ color: '#6B7280' }}>Taxes</strong> = Base Payment × Tax Rate
                        </p>
                        <p className="mb-5 text-xs" style={{ color: '#6B7280' }}>
                          = ${leaseResults.basePayment.toFixed(2)} × {leaseInputs.taxRate}%
                          <br />= <strong>${(leaseResults.totalMonthlyPayment - leaseResults.basePayment).toFixed(2)}/mo</strong>
                        </p>
                        <p className="mb-3" style={{ color: '#1F2937' }}>
                          <strong style={{ color: '#001F3F' }}>Final Monthly Payment</strong> = Base + Taxes
                        </p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>
                          = ${leaseResults.basePayment.toFixed(2)} + ${(leaseResults.totalMonthlyPayment - leaseResults.basePayment).toFixed(2)}
                          <br />= <strong style={{ color: '#001F3F', fontSize: '14px' }}>${leaseResults.totalMonthlyPayment.toFixed(2)}/mo</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Notes */}
              <div className="pt-8 pb-12" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Combined Notes Card */}
                <div 
                  className="p-5 flex items-start gap-3"
                  style={{ 
                    backgroundColor: '#FFFFFF', 
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    padding: '16px 20px',
                    boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                  }}
                >
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#001F3F' }} strokeWidth={2} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                      Note: Most leases also have a disposition fee ($300–$500) when you return the car. Expect possible wear-and-tear charges and over-mileage fees ($0.15–$0.25 per mile) if you go over the allowed miles (usually 10k–15k per year). You can usually buy the car at the residual value if you love it.
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                      Note: Leasing often requires higher insurance and gap coverage than buying. Factor those into your monthly budget.
                    </p>
                  </div>
                </div>
                
                {/* Final Disclaimer */}
                <div className="pt-6">
                  <p className="text-sm text-center leading-relaxed" style={{ color: '#9CA3AF' }}>
                    Educational use only. Not financial, legal, or tax advice. Always verify numbers with your dealer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Check Dealer Lease Quote Modal */}
      {showLeaseChecker && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ backgroundColor: '#F8F9FB' }}
          data-testid="lease-checker-modal"
        >
          {/* Navigation Bar */}
          <div 
            className="sticky top-0 z-10"
            style={{ 
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E8EAED',
            }}
          >
            <div className="max-w-screen-sm mx-auto px-5 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowLeaseChecker(false);
                    setLeaseCheckResults(null);
                  }}
                  className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                  style={{ color: BRAND.navy }}
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2} />
                  <span className="font-medium">Back</span>
                </button>
                <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
                  VINdicated.<span style={{ color: BRAND.red }}>ai</span>
                </span>
              </div>
            </div>
          </div>

          {/* Hero - Gradient Banner */}
          <div style={{ backgroundColor: '#FFFFFF' }}>
            <div className="max-w-screen-sm mx-auto px-5 pt-6 pb-4">
              <div 
                style={{ 
                  background: 'linear-gradient(135deg, #001F3F, #1E3A8A)', 
                  borderRadius: '20px', 
                  padding: '28px 24px',
                  marginBottom: '16px',
                }}
              >
                <h1 className="font-bold mb-2" style={{ color: '#FFFFFF', fontSize: '22px' }}>
                  Check My Dealer Lease Quote
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.6' }}>
                  Enter the dealer&apos;s numbers and we&apos;ll reverse-engineer them to spot any tricks or markups.
                </p>
              </div>

              {/* How this works - Collapsible Accordion */}
              <div 
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(0,31,63,0.06)',
                  marginBottom: '16px',
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => setShowLeaseCheckerExplainer(!showLeaseCheckerExplainer)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex items-center justify-center flex-shrink-0" 
                      style={{ 
                        width: '36px', 
                        height: '36px', 
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                      }}
                    >
                      <Lightbulb className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                    </div>
                    <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>How this works</span>
                  </div>
                  <ChevronDown 
                    className="w-5 h-5 transition-transform" 
                    style={{ 
                      color: '#6B7280',
                      transform: showLeaseCheckerExplainer ? 'rotate(180deg)' : 'rotate(0deg)',
                    }} 
                  />
                </button>
                {showLeaseCheckerExplainer && (
                  <div style={{ padding: '0 16px 16px 16px' }}>
                    <p className="text-sm leading-relaxed" style={{ color: '#1F2937', lineHeight: '1.75' }}>
                      Imagine leasing a car is like renting an apartment, but with some sneaky math tricks. The dealer tells you &quot;Rent is $450/month.&quot; This tool asks: What&apos;s the apartment really worth (MSRP), how much of it am I actually paying for (cap cost), and what&apos;s the secret interest rate they&apos;re charging me (money factor)? It runs the numbers backward and tells you if it&apos;s a great deal — or if something looks off. Always get the full worksheet to be 100% sure.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-screen-sm mx-auto px-5 pb-6">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* What to Ask the Dealer Checklist Card */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
                  padding: '20px',
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div 
                    className="flex items-center justify-center flex-shrink-0" 
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                    }}
                  >
                    <ClipboardList className="w-5 h-5" style={{ color: '#FFFFFF' }} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold" style={{ color: BRAND.navy }}>Before You Use This Tool — Get These Numbers</h3>
                    <p className="text-sm mt-1" style={{ color: '#1F2937' }}>
                      Ask the dealer for each of these. If they won&apos;t give you the money factor, that&apos;s a red flag.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
                  {[
                    "The monthly payment — and whether it includes tax",
                    "The MSRP (sticker price) of the vehicle",
                    "The residual value or residual percentage",
                    "The money factor (this is the hidden interest rate — multiply by 2,400 to get the APR)",
                    "The selling price or adjusted cap cost (the negotiated price after incentives and fees)",
                    "Your local sales tax rate",
                    "The full lease worksheet or deal sheet — in writing"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div 
                        style={{ 
                          width: '16px', 
                          height: '16px', 
                          border: '2px solid #001F3F', 
                          borderRadius: '3px', 
                          backgroundColor: 'white',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      />
                      <span className="text-sm" style={{ color: '#1F2937' }}>{item}</span>
                    </div>
                  ))}
                </div>
                
                <p className="mt-4" style={{ fontSize: '13px', color: '#1F2937', lineHeight: '1.6' }}>
                  If the dealer gives you all of these numbers, you can plug them in below and see exactly whether the deal is fair. If they refuse to share the money factor or the worksheet, that tells you something about how they do business.
                </p>
              </div>

              {/* Input Card */}
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(0,31,63,0.06)',
                  padding: '20px',
                }}
              >
                <h2 className="font-bold mb-5" style={{ color: BRAND.navy, fontSize: '17px' }}>
                  Enter the dealer&apos;s numbers
                </h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Monthly Payment Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium" style={{ color: '#1F2937' }}>Dealer quoted monthly payment</label>
                      <TapToTypeValue
                        value={leaseCheckInputs.quotedPayment}
                        onChange={(val) => setLeaseCheckInputs({...leaseCheckInputs, quotedPayment: val})}
                        min={100}
                        max={1500}
                        prefix="$"
                      />
                    </div>
                    <input
                      type="range"
                      min={100}
                      max={1500}
                      step={10}
                      value={leaseCheckInputs.quotedPayment}
                      onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, quotedPayment: parseInt(e.target.value)})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseCheckInputs.quotedPayment - 100) / 1400) * 100}%, #E5E7EB ${((leaseCheckInputs.quotedPayment - 100) / 1400) * 100}%, #E5E7EB 100%)`,
                      }}
                    />
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="checkbox"
                        id="includesTax"
                        checked={leaseCheckInputs.paymentIncludesTax}
                        onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, paymentIncludesTax: e.target.checked})}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: BRAND.navy }}
                      />
                      <label htmlFor="includesTax" className="text-sm" style={{ color: '#6B7280' }}>
                        Payment includes tax
                      </label>
                    </div>
                  </div>

                  {/* MSRP Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-1.5">
                        <label className="text-sm font-medium" style={{ color: '#1F2937' }}>MSRP</label>
                        <button
                          onClick={() => setLeaseCheckerTooltipOpen(leaseCheckerTooltipOpen === 'msrp' ? null : 'msrp')}
                          className="hover:opacity-70"
                        >
                          <Info className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseCheckInputs.msrp}
                        onChange={(val) => setLeaseCheckInputs({...leaseCheckInputs, msrp: val})}
                        min={5000}
                        max={100000}
                        prefix="$"
                      />
                    </div>
                    {leaseCheckerTooltipOpen === 'msrp' && (
                      <div 
                        className="mb-3 p-4 rounded-xl relative"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #001F3F', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                      >
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          The manufacturer&apos;s suggested retail price — the sticker price before negotiation.
                        </p>
                        <button onClick={() => setLeaseCheckerTooltipOpen(null)} className="absolute top-3 right-3" style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}>
                          ×
                        </button>
                      </div>
                    )}
                    <input
                      type="range"
                      min={5000}
                      max={100000}
                      step={500}
                      value={leaseCheckInputs.msrp}
                      onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, msrp: parseInt(e.target.value)})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseCheckInputs.msrp - 5000) / 95000) * 100}%, #E5E7EB ${((leaseCheckInputs.msrp - 5000) / 95000) * 100}%, #E5E7EB 100%)`,
                      }}
                    />
                  </div>

                  {/* Residual % Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-1.5">
                        <label className="text-sm font-medium" style={{ color: '#1F2937' }}>Residual %</label>
                        <button
                          onClick={() => setLeaseCheckerTooltipOpen(leaseCheckerTooltipOpen === 'residual' ? null : 'residual')}
                          className="hover:opacity-70"
                        >
                          <Info className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseCheckInputs.residualPercent}
                        onChange={(val) => setLeaseCheckInputs({...leaseCheckInputs, residualPercent: val})}
                        min={30}
                        max={70}
                        suffix="%"
                      />
                    </div>
                    {leaseCheckerTooltipOpen === 'residual' && (
                      <div 
                        className="mb-3 p-4 rounded-xl relative"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #001F3F', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                      >
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          The percentage of MSRP the car is worth at lease end. Higher residual = lower payment. Dealers don&apos;t negotiate this — it&apos;s set by the manufacturer.
                        </p>
                        <button onClick={() => setLeaseCheckerTooltipOpen(null)} className="absolute top-3 right-3" style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}>
                          ×
                        </button>
                      </div>
                    )}
                    <input
                      type="range"
                      min={30}
                      max={70}
                      step={1}
                      value={leaseCheckInputs.residualPercent}
                      onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, residualPercent: parseInt(e.target.value)})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseCheckInputs.residualPercent - 30) / 40) * 100}%, #E5E7EB ${((leaseCheckInputs.residualPercent - 30) / 40) * 100}%, #E5E7EB 100%)`,
                      }}
                    />
                  </div>

                  {/* Term */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>Term (months)</label>
                    <div className="flex gap-2">
                      {[24, 36, 48, 60].map((term) => (
                        <button
                          key={term}
                          onClick={() => setLeaseCheckInputs({...leaseCheckInputs, termMonths: term})}
                          className="flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all"
                          style={{ 
                            backgroundColor: leaseCheckInputs.termMonths === term ? BRAND.navy : '#F3F4F6',
                            color: leaseCheckInputs.termMonths === term ? '#FFFFFF' : '#6B7280',
                          }}
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Selling Price / Adjusted Cap Cost - Combined Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-1.5">
                        <label className="text-sm font-medium" style={{ color: '#1F2937' }}>Selling Price or Adjusted Cap Cost</label>
                        <button
                          onClick={() => setLeaseCheckerTooltipOpen(leaseCheckerTooltipOpen === 'capcost' ? null : 'capcost')}
                          className="hover:opacity-70"
                        >
                          <Info className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseCheckInputs.capCostOrSellingPrice}
                        onChange={(val) => setLeaseCheckInputs({...leaseCheckInputs, capCostOrSellingPrice: val})}
                        min={0}
                        max={100000}
                        prefix="$"
                      />
                    </div>
                    <p className="text-xs mb-3" style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.5' }}>
                      Enter the negotiated selling price. If the dealer gave you an adjusted cap cost on their worksheet, use that instead — it&apos;s the more precise number.
                    </p>
                    {leaseCheckerTooltipOpen === 'capcost' && (
                      <div 
                        className="mb-3 p-4 rounded-xl relative"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #001F3F', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                      >
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          Your negotiated price. Lower is better. This is the #1 number to negotiate on a lease.
                        </p>
                        <button onClick={() => setLeaseCheckerTooltipOpen(null)} className="absolute top-3 right-3" style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}>
                          ×
                        </button>
                      </div>
                    )}
                    <input
                      type="range"
                      min={0}
                      max={100000}
                      step={500}
                      value={leaseCheckInputs.capCostOrSellingPrice}
                      onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, capCostOrSellingPrice: parseInt(e.target.value)})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(leaseCheckInputs.capCostOrSellingPrice / 100000) * 100}%, #E5E7EB ${(leaseCheckInputs.capCostOrSellingPrice / 100000) * 100}%, #E5E7EB 100%)`,
                      }}
                    />
                  </div>

                  {/* Money Factor Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-1.5">
                        <label className="text-sm font-medium" style={{ color: '#1F2937' }}>Money Factor</label>
                        <button
                          onClick={() => setLeaseCheckerTooltipOpen(leaseCheckerTooltipOpen === 'moneyfactor' ? null : 'moneyfactor')}
                          className="hover:opacity-70"
                        >
                          <Info className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
                        </button>
                      </div>
                      <TapToTypeValue
                        value={leaseCheckInputs.moneyFactor}
                        onChange={(val) => setLeaseCheckInputs({...leaseCheckInputs, moneyFactor: val})}
                        min={0.00001}
                        max={0.005}
                        decimals={5}
                        formatCommas={false}
                      />
                    </div>
                    {leaseCheckerTooltipOpen === 'moneyfactor' && (
                      <div 
                        className="mb-3 p-4 rounded-xl relative"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #001F3F', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                      >
                        <p className="text-sm leading-relaxed mb-2" style={{ color: '#1F2937' }}>
                          The hidden interest rate on your lease. Multiply by 2,400 to convert to APR. Example: 0.00125 × 2,400 = 3.0% APR. Ask the dealer for this number — they must provide it.
                        </p>
                        <a 
                          href="/public-tools?openLeaseCalc=true" 
                          className="text-sm font-medium flex items-center gap-1"
                          style={{ color: BRAND.red }}
                        >
                          Learn how money factor works <ArrowRight className="w-3 h-3" />
                        </a>
                        <button onClick={() => setLeaseCheckerTooltipOpen(null)} className="absolute top-3 right-3" style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}>
                          ×
                        </button>
                      </div>
                    )}
                    <input
                      type="range"
                      min={0.00001}
                      max={0.005}
                      step={0.00001}
                      value={leaseCheckInputs.moneyFactor}
                      onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, moneyFactor: parseFloat(e.target.value)})}
                      className="budget-slider"
                      style={{
                        width: '100%',
                        height: '6px',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((leaseCheckInputs.moneyFactor - 0.00001) / 0.00499) * 100}%, #E5E7EB ${((leaseCheckInputs.moneyFactor - 0.00001) / 0.00499) * 100}%, #E5E7EB 100%)`,
                      }}
                    />
                    <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                      = {(leaseCheckInputs.moneyFactor * 2400).toFixed(2)}% APR
                    </p>
                  </div>

                  {/* Tax Rate Slider & Tax Mode */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium" style={{ color: '#1F2937' }}>Tax Rate %</label>
                        <TapToTypeValue
                          value={leaseCheckInputs.taxRate}
                          onChange={(val) => setLeaseCheckInputs({...leaseCheckInputs, taxRate: val})}
                          min={0}
                          max={12}
                          suffix="%"
                          decimals={1}
                        />
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={12}
                        step={0.1}
                        value={leaseCheckInputs.taxRate}
                        onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, taxRate: parseFloat(e.target.value)})}
                        className="budget-slider"
                        style={{
                          width: '100%',
                          height: '6px',
                          cursor: 'pointer',
                          background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(leaseCheckInputs.taxRate / 12) * 100}%, #E5E7EB ${(leaseCheckInputs.taxRate / 12) * 100}%, #E5E7EB 100%)`,
                        }}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <label className="block text-sm font-medium" style={{ color: '#1F2937' }}>Tax mode</label>
                        <button
                          onClick={() => setLeaseCheckerTooltip(leaseCheckerTooltip === 'taxmode' ? null : 'taxmode')}
                          className="hover:opacity-70"
                        >
                          <Info className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
                        </button>
                      </div>
                      <select
                        value={leaseCheckInputs.taxMode}
                        onChange={(e) => setLeaseCheckInputs({...leaseCheckInputs, taxMode: e.target.value})}
                        className="w-full px-3 py-2.5 text-sm font-medium rounded-xl"
                        style={{ border: '1.5px solid #E5E7EB', backgroundColor: '#FAFBFC', color: BRAND.navy }}
                      >
                        <option value="monthly">Tax monthly</option>
                        <option value="upfront">Tax upfront</option>
                      </select>
                      {leaseCheckerTooltip === 'taxmode' && (
                        <div 
                          className="mt-2 p-4 rounded-xl relative"
                          style={{ backgroundColor: '#FFFFFF', border: '1px solid #001F3F', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                        >
                          <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                            In most states tax monthly saves you money because you only pay tax on the portion of the car you&apos;re using each month. Some dealers hide extra fees here.
                          </p>
                          <button 
                            onClick={() => setLeaseCheckerTooltip(null)}
                            className="absolute top-3 right-3"
                            style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reverse Engineer Button */}
                  <button
                    onClick={() => {
                      reverseEngineerLease();
                      // Smooth scroll to results after a brief delay
                      setTimeout(() => {
                        const resultsSection = document.getElementById('lease-checker-results');
                        if (resultsSection) {
                          resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    className="w-full py-3.5 font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.99]"
                    style={{ 
                      backgroundColor: BRAND.navy, 
                      color: '#FFFFFF',
                      boxShadow: '0 4px 12px rgba(0,31,63,0.20)',
                    }}
                  >
                    <span>Reverse Engineer</span>
                    <ArrowRight className="w-4 h-4" style={{ color: BRAND.red }} strokeWidth={2.5} />
                  </button>

                  {/* Pro tip card - prominent styling */}
                  <div 
                    className="flex items-start gap-3 mt-4"
                    style={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #FF4136', 
                      borderRadius: '12px', 
                      boxShadow: '0 2px 12px rgba(255,65,54,0.10)', 
                      padding: '16px',
                    }}
                  >
                    <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: '#FF4136' }} />
                    <p style={{ fontSize: '15px', color: '#1F2937', lineHeight: '1.6' }}>
                      <span className="font-bold" style={{ color: '#FF4136' }}>Pro tip:</span> Always get the full worksheet (not just the payment). Reply to the dealer: &quot;Hey, can you email me the full lease worksheet so I can verify the cap cost, fees, and money factor?&quot; Most dealers will send it once you sound like you know what you&apos;re doing.
                    </p>
                  </div>
                </div>
              </div>

              {/* Results Card - Premium Redesign */}
              {leaseCheckResults && (
                <div
                  id="lease-checker-results"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 4px 20px rgba(0,31,63,0.08)',
                    padding: '24px',
                  }}
                >
                  {/* Hero Spotlight - Deal Score */}
                  {leaseCheckResults.leaseScore && (
                    <div 
                      className="text-center"
                      style={{ 
                        backgroundColor: '#F8F9FB',
                        borderRadius: '16px',
                        padding: '32px 24px',
                        marginBottom: '20px',
                      }}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#6B7280' }}>
                        Quick Lease Deal Score
                      </p>
                      <p 
                        className="font-bold mb-2" 
                        style={{ 
                          fontSize: '48px', 
                          lineHeight: '1',
                          color: leaseCheckResults.leaseScore.ratio <= 1.0 ? '#001F3F' : 
                                 leaseCheckResults.leaseScore.ratio <= 1.25 ? '#001F3F' : 
                                 leaseCheckResults.leaseScore.ratio <= 1.5 ? '#1F2937' : '#FF4136'
                        }}
                      >
                        {leaseCheckResults.leaseScore.label}
                      </p>
                      <p style={{ fontSize: '14px', color: '#6B7280' }}>
                        Payment ÷ MSRP = {leaseCheckResults.leaseScore.ratio.toFixed(2)}% <span style={{ color: '#9CA3AF' }}>(target ≤1.0%)</span>
                      </p>
                      {/* Warning if red flags exist */}
                      {leaseCheckResults.redFlags.length > 0 && (
                        <p className="mt-4" style={{ fontSize: '13px', color: '#FF4136' }}>
                          Warning: This score only measures the payment amount. See the red flags below — your rate or fees may still be too high.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Horizontal Stat Pills Row */}
                  {leaseCheckResults.impliedMF && (
                    <div 
                      style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        marginBottom: '20px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Money Factor Pill */}
                      <div 
                        className="relative"
                        style={{ 
                          flex: '1 1 0',
                          minWidth: '100px',
                          backgroundColor: '#F8F9FB',
                          borderRadius: '12px',
                          padding: '16px',
                          textAlign: 'center',
                        }}
                      >
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <p style={{ color: '#6B7280', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>MF</p>
                          <button
                            onClick={() => setLeaseCheckerTooltip(leaseCheckerTooltip === 'mf' ? null : 'mf')}
                            className="hover:opacity-70"
                          >
                            <Info className="w-3 h-3" style={{ color: '#94A3B8' }} />
                          </button>
                        </div>
                        <p className="font-bold" style={{ color: BRAND.navy, fontSize: '18px' }}>
                          {leaseCheckResults.impliedMF.toFixed(5)}
                        </p>
                        {leaseCheckerTooltip === 'mf' && (
                          <div 
                            className="absolute left-0 right-0 top-full mt-2 p-4 rounded-xl z-10 text-left"
                            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                          >
                            <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                              Money factor × 2400 = actual APR. If the dealer quoted you 0.0025 but the tool shows 0.0035… they marked it up. Walk away or negotiate it down.
                            </p>
                            <button 
                              onClick={() => setLeaseCheckerTooltip(null)}
                              className="absolute top-3 right-3"
                              style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>

                      {/* APR Pill */}
                      <div 
                        style={{ 
                          flex: '1 1 0',
                          minWidth: '100px',
                          backgroundColor: '#F8F9FB',
                          borderRadius: '12px',
                          padding: '16px',
                          textAlign: 'center',
                        }}
                      >
                        <p style={{ color: '#6B7280', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>APR</p>
                        <p className="font-bold" style={{ 
                          color: leaseCheckResults.impliedAPR <= 4.0 ? '#001F3F' : leaseCheckResults.impliedAPR <= 6.0 ? '#1F2937' : '#FF4136', 
                          fontSize: '18px' 
                        }}>
                          {leaseCheckResults.impliedAPR.toFixed(2)}%
                        </p>
                        {leaseCheckResults.impliedAPR > 6.0 && (
                          <p style={{ color: '#FF4136', fontSize: '10px', marginTop: '4px' }}>High</p>
                        )}
                      </div>

                      {/* Cap Cost Pill */}
                      {leaseCheckResults.impliedCapCost && (
                        <div 
                          className="relative"
                          style={{ 
                            flex: '1 1 0',
                            minWidth: '100px',
                            backgroundColor: '#F8F9FB',
                            borderRadius: '12px',
                            padding: '16px',
                            textAlign: 'center',
                          }}
                        >
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <p style={{ color: '#6B7280', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cap Cost</p>
                            <button
                              onClick={() => setLeaseCheckerTooltip(leaseCheckerTooltip === 'cap' ? null : 'cap')}
                              className="hover:opacity-70"
                            >
                              <Info className="w-3 h-3" style={{ color: '#94A3B8' }} />
                            </button>
                          </div>
                          <p className="font-bold" style={{ color: BRAND.navy, fontSize: '18px' }}>
                            ${leaseCheckResults.impliedCapCost.toLocaleString()}
                          </p>
                          {leaseCheckerTooltip === 'cap' && (
                            <div 
                              className="absolute left-0 right-0 top-full mt-2 p-4 rounded-xl z-10 text-left"
                              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                            >
                              <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                                If you have a trade-in, big down payment, or rebates, put that number here. Suddenly your score improves dramatically. This is how people turn a &quot;meh&quot; quote into a &quot;Great deal.&quot;
                              </p>
                              <button 
                                onClick={() => setLeaseCheckerTooltip(null)}
                                className="absolute top-3 right-3"
                                style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* APR Warning Message - Shown below pills when relevant */}
                  {leaseCheckResults.impliedAPR > 4.0 && leaseCheckResults.impliedAPR <= 6.0 && (
                    <p className="text-center mb-4" style={{ color: '#1F2937', fontSize: '13px' }}>
                      APR is above average — ask the dealer if a lower rate is available.
                    </p>
                  )}
                  {leaseCheckResults.impliedAPR > 6.0 && (
                    <p className="text-center mb-4" style={{ color: '#FF4136', fontSize: '13px' }}>
                      High rate — this is costing you significantly more each month. Ask for the buy rate.
                    </p>
                  )}

                  {/* Cap Cost Comparison Insight Card - Full Card */}
                  {leaseCheckResults.impliedCapCost && leaseCheckResults.sellingPriceInput > 0 && (() => {
                    const gap = leaseCheckResults.sellingPriceInput - leaseCheckResults.impliedCapCost;
                    const absGap = Math.abs(gap);
                    
                    if (gap > 200) {
                      // Implied cap cost is MORE than $200 below selling price
                      return (
                        <div 
                          className="lease-checker-result-card"
                          style={{ 
                            backgroundColor: '#FFFFFF', 
                            border: '1px solid #E5E7EB',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                            padding: '16px 20px',
                            marginBottom: '16px',
                          }}
                        >
                          <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                            <span style={{ color: '#1F2937' }}>Based on your quoted payment, the dealer is pricing this car at </span>
                            <span className="font-bold" style={{ color: '#1F2937' }}>${leaseCheckResults.impliedCapCost.toLocaleString()}</span>
                            <span style={{ color: '#1F2937' }}>. That&apos;s </span>
                            <span className="font-bold" style={{ color: '#1F2937' }}>${absGap.toLocaleString()}</span>
                            <span style={{ color: '#1F2937' }}> below the </span>
                            <span className="font-bold" style={{ color: '#1F2937' }}>${leaseCheckResults.sellingPriceInput.toLocaleString()}</span>
                            <span style={{ color: '#1F2937' }}> you entered. This could mean a discount — or it could mean the numbers the dealer gave you don&apos;t match the actual payment math. Ask them to walk you through the worksheet line by line.</span>
                          </p>
                        </div>
                      );
                    } else if (gap < -200) {
                      // Implied cap cost is MORE than $200 above selling price
                      return (
                        <div 
                          className="lease-checker-result-card"
                          style={{ 
                            backgroundColor: '#FFFFFF', 
                            border: '1px solid #FF4136',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                            padding: '16px 20px',
                            marginBottom: '16px',
                          }}
                        >
                          <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                            <span style={{ color: '#1F2937' }}>Based on your quoted payment, the implied cap cost is </span>
                            <span className="font-bold" style={{ color: '#FF4136' }}>${leaseCheckResults.impliedCapCost.toLocaleString()}</span>
                            <span style={{ color: '#1F2937' }}> — that&apos;s </span>
                            <span className="font-bold" style={{ color: '#FF4136' }}>${absGap.toLocaleString()} MORE</span>
                            <span style={{ color: '#1F2937' }}> than the </span>
                            <span className="font-bold" style={{ color: '#1F2937' }}>${leaseCheckResults.sellingPriceInput.toLocaleString()}</span>
                            <span style={{ color: '#1F2937' }}> you entered. The payment doesn&apos;t match the stated price. Ask the dealer to walk you through the worksheet line by line.</span>
                          </p>
                        </div>
                      );
                    } else {
                      // Within ±$200
                      return (
                        <div 
                          className="lease-checker-result-card"
                          style={{ 
                            backgroundColor: '#FFFFFF', 
                            border: '1px solid #E5E7EB',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                            padding: '16px 20px',
                            marginBottom: '16px',
                          }}
                        >
                          <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                            <span style={{ color: '#1F2937' }}>The payment matches the selling price you entered — the numbers add up.</span>
                          </p>
                        </div>
                      );
                    }
                  })()}

                  {/* Budget Impact Button */}
                  <div className="relative" style={{ marginBottom: '16px' }}>
                    <a
                      href="/tools/budget"
                      className="lease-checker-result-card w-full py-3 px-4 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-all hover:opacity-90"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                        color: BRAND.navy,
                      }}
                    >
                      <span>See how ${leaseCheckResults.quotedPayment || leaseCheckInputs.quotedPayment}/month affects my Savings &amp; Fun Money sliders</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setLeaseCheckerTooltip(leaseCheckerTooltip === 'budget' ? null : 'budget');
                        }}
                        className="hover:opacity-70 ml-1"
                      >
                        <Info className="w-3.5 h-3.5" style={{ color: BRAND.navy }} />
                      </button>
                    </a>
                    {leaseCheckerTooltip === 'budget' && (
                      <div 
                        className="absolute left-0 right-0 top-full mt-2 p-4 rounded-xl z-10 text-left"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 4px 16px rgba(0,31,63,0.12)' }}
                      >
                        <p className="text-sm leading-relaxed" style={{ color: '#1F2937' }}>
                          See how this payment affects my Savings &amp; Fun Money sliders.
                        </p>
                        <button 
                          onClick={() => setLeaseCheckerTooltip(null)}
                          className="absolute top-3 right-3"
                          style={{ color: '#001F3F', fontSize: '16px', cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Red Flags */}
                  {leaseCheckResults.redFlags.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: BRAND.navy }}>
                        Red flags
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {leaseCheckResults.redFlags.map((flag, index) => (
                          <div 
                            key={index}
                            className="lease-checker-result-card flex items-start gap-3 p-4 rounded-xl"
                            style={{ 
                              backgroundColor: '#FFFFFF', 
                              border: '1px solid #FF4136',
                              borderRadius: '12px',
                              boxShadow: '0 2px 12px rgba(255,65,54,0.10)',
                            }}
                          >
                            <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: '#FF4136' }} />
                            <p className="text-sm" style={{ color: '#1F2937' }}>
                              <span style={{ color: '#FF4136' }}>{flag}</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {leaseCheckResults.redFlags.length === 0 && (
                    <div 
                      className="lease-checker-result-card flex items-center gap-3 p-4 rounded-xl"
                      style={{ 
                        backgroundColor: '#FFFFFF', 
                        border: '1px solid #001F3F',
                        borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
                      }}
                    >
                      <CheckCircle className="w-6 h-6" style={{ color: '#001F3F' }} />
                      <p className="text-sm" style={{ color: '#1F2937' }}>
                        No obvious red flags detected. The numbers look reasonable.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicTools;
